import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import PageHeader from './components/PageHeader';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { usePendingPrintRequest } from './hooks/usePendingPrintRequest';
import useMetrcSignInCheck from './hooks/useMetrcSignInCheck';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import {
  AUTH_STORAGE_KEY,
  DEFAULT_API_BASE_URL,
  DEFAULT_PAGE_SIZE,
} from './constants';
import {
  createFormatTextContent,
  createResolveBarcodeData,
  createResolveImageUrl,
  fetchImageBytes,
  generateLabelPdf,
  revokePdfUrl,
  computeLayout,
  pdfFontSources,
  DEFAULT_FONT,
} from './wysiwyg/services';
import { defaultPreviewLayouts } from './wysiwyg/previewLayouts';
import { usePreviewLayouts } from './hooks/usePreviewLayouts';
import {
  createLegacyAssetResolver,
  preloadNewAssets,
  type LegacyAssetResolver,
} from './wysiwyg/legacyAssets';
import type {
  Template,
  PreviewLayout,
  ThemeVariable,
  DateKind,
  DateVariableState,
  ImageElement,
  TextElement,
} from './wysiwyg/types';
import type { PrintRunRecord, TemplateRecord } from './types/index';
import type { PrintPackageMode } from './types/multiPackagePrint';
import type { FormatResolvers } from './wysiwyg/formats/types';
import type { AssetApi } from './wysiwyg/assetService';
import { type PackageCoaOption, type Product } from './wysiwyg/packageUtils';
import { normalizeCoaKeys } from './wysiwyg/coaUtils';
import { buildConditionTypeByKey } from './wysiwyg/conditions';
import { getEditableCoaPayload } from './utils/coaRecords';
import useIssuedPrintCoa, { useIssuedStagedPrintCoa } from './hooks/useIssuedPrintCoa';
import useDraftPrintCoa, { decodeDraftPrintCoa, positiveId } from './hooks/useDraftPrintCoa';
import { parseDisplayDate } from './utils/parseDisplayDate';
import { resolveProductFields } from './utils/resolveProductFields';
import {
  generateRetailIdQrUrlsSequential,
  generateRetailIdQrUrlsSequentialProgressive,
  isValidPackageTag,
} from './wysiwyg/retailIdQr';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import exportPrintRunJob, { exportPrintRunReel } from './utils/exportPrintRunJob';
import { printRunCsvFilename, printRunFilenameBase, printRunPackageFilenameBase } from './utils/printRunFilename';
import DownloadActionIcon from './components/DownloadActionIcon';
import HourglassIcon from './components/HourglassIcon';
import { createPdfCacheId, storePdf, getPdf, clearPdfs } from './utils/pdfStorage';
import { PdfWorkerPool, getEffectiveWorkerCount, isWorkerSupported } from './workers/pdfWorkerPool';
import type { SharedWorkerData } from './workers/pdfWorkerPool';
import type { ReelAssignment, SerializableResolverContext } from './workers/types';
import { DEFAULT_WORKER_COUNT } from './workers/types';
import { computeMiniReels, shouldUseMiniReels } from './utils/reelSplitter';
import { mergeAndStreamPdfs } from './utils/pdfMerger';
import { prepareRenderData } from './wysiwyg/templateRuntime';
import TacValueEditor from './components/TacValueEditor';
import { getTacAdjustment, isStagedTacPackageKey, rebindStagedTacAdjustment, resolveTemplateTac, restoreSavedTacContext, tacPackageKey, templateAllowsTacCalculation, templateUsesTac, withTacMarketPolicy, withTacPackageIdentity, withTacSources, type TacAdjustment } from './utils/tac';
import useMarkets from './hooks/useMarkets';
import { resolveTacPrintOutput, type TacPrintOutput } from './wysiwyg/services/tacPrintAudit';
import { normalizeLoadedTemplate } from './wysiwyg/templateNormalization';
import { getFileContentUrl, getFileLink, getStaticFiles, uploadFileId } from './hooks/useFiles';
import {
  getCOAByPackageId,
  resolveRestoredPackageLookup,
  usePrintPackageSearch,
} from './hooks/usePackages';
import {
  addPrintRun,
  editPrintRun,
  recordPrintRunError,
  saveDraftPrintRun,
} from './hooks/usePrintRuns';
import type { DeleteDraftPrintRun } from './hooks/usePrintRuns';
import useAvailablePrintTemplates from './hooks/useAvailablePrintTemplates';
import { useProduct } from './hooks/useProducts';
import useProductCatalog from './hooks/useProductCatalog';
import useReassignPrintRun from './hooks/useReassignPrintRun';
import { getErrorMessage } from './utils/requestErrors';
import { useSsoReauth } from './contexts/SsoReauthContext';
import { validatePrintRunOutput } from './hooks/usePrintRuns';
import {
  isActiveProduct,
  getActiveProductOptions,
  getProductOptions,
  getProductOverride,
} from './utils/productOverrides';
import ProductSelectionModal from './components/ProductSelectionModal';
import { getFormatOrDefault } from './wysiwyg/formats/index';
import {
  getEffectiveShelfLifeDays,
  getDateSourceSignature,
  mergePackageDateSources,
  withCalculatedPrintDates,
  getUsedPrintDateVariables,
  resolveAutomaticPrintDate,
} from './utils/printDates';
import MultiPackagePrintFeature from './features/multiPackagePrint/MultiPackagePrintFeature';
import { isMultiPackageLabelGenerationEnabled } from './features/multiPackagePrint/capabilities';

const pageSize = DEFAULT_PAGE_SIZE;
const AUTO_LAYOUT_ID = 'auto-label-size';
const DRAFT_SAVE_IDENTITY_ERROR = 'The draft may have been saved, but its response has no valid draft ID. Close this editor and reopen the draft from Print Runs before continuing.';
const EMPTY_API_TEMPLATES: ApiTemplate[] = [];
type DraftOutputMode = 'labels+csv' | 'csv';

type DraftPrintSettings = {
  outputMode: DraftOutputMode;
  splitIntoReels: boolean;
  labelsPerReel: number;
};

function isComposingKeyEvent(event: KeyboardEvent) {
  return event.isComposing || event.keyCode === 229;
}

function scrollActivePickerOptionIntoView(pickerId: string, activeIndex: number) {
  const option = document.getElementById(`${pickerId}-option-${activeIndex}`);
  const list = option?.parentElement;
  if (!option || !list) return;

  const optionRect = option.getBoundingClientRect();
  const listRect = list.getBoundingClientRect();
  if (optionRect.top < listRect.top) {
    list.scrollTop += optionRect.top - listRect.top;
  } else if (optionRect.bottom > listRect.bottom) {
    list.scrollTop += optionRect.bottom - listRect.bottom;
  }
}

function handleSearchPickerKeyDown<T>(
  event: React.KeyboardEvent<HTMLInputElement>,
  {
    options,
    activeIndex,
    setActiveIndex,
    selectOption,
    closePicker,
  }: {
    options: T[];
    activeIndex: number;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    selectOption: (option: T) => void;
    closePicker: (restoreFocus?: boolean) => void;
  },
) {
  event.stopPropagation();
  if (isComposingKeyEvent(event.nativeEvent)) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closePicker(true);
  } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    if (options.length === 0) return;
    const step = event.key === 'ArrowDown' ? 1 : -1;
    setActiveIndex((current) => {
      const base = current < 0 ? 0 : current;
      return (base + step + options.length) % options.length;
    });
  } else if (event.key === 'Enter') {
    event.preventDefault();
    const option = options[activeIndex];
    if (option) selectOption(option);
  }
}

function ThemeVariableInput({
  variable,
  value,
  onChange,
  disabled = false,
  testId,
}: {
  variable: ThemeVariable;
  value: string | number | boolean;
  onChange: (value: string | number | boolean) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: '#fff',
    color: disabled ? '#808080' : 'inherit',
    fontSize: 13,
    width: '100%',
    maxWidth: 200,
  };

  switch (variable.type) {
    case 'boolean':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
            data-testid={testId}
          />
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{value ? 'Yes' : 'No'}</span>
        </label>
      );

    case 'number':
      return (
        <input
          type="number"
          value={typeof value === 'number' ? value : Number(value) || 0}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          disabled={disabled}
          style={inputStyle}
          data-testid={testId}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={inputStyle}
          data-testid={testId}
        />
      );

    case 'select':
      return (
        <select
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{ ...inputStyle, cursor: 'pointer' }}
          data-testid={testId}
        >
          {(variable.options || [])
            .map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </select>
      );

    case 'string':
    default:
      return (
        <input
          type="text"
          value={String(value || '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={inputStyle}
          data-testid={testId}
        />
      );
  }
}

type ApiTemplate = {
  id: string | number;
  name: string;
  content?: string;
  /** Legacy: some code paths may use 'data' instead */
  data?: string | Template;
};

type InitialValues = {
  name?: string;
  jobId?: string;
  printRunId?: number;
  templateId?: number;
  packageId?: string | number | null;
  packageLabel?: string;
  productName?: string;
  count?: number;
  printedDate?: string;
  packagedDate?: string;
  packageTag?: string;
  isStaged?: boolean;
  stagedCoaId?: number | string | null;
  stagedCoaData?: string | null;
  stagedPackage?: any;
  status?: number | string;
};

type StartPrintDialogProps = {
  apiTemplates?: ApiTemplate[];
  templates?: string[];
  defaultJobId?: string;
  initialValues?: InitialValues;
  /** Restored print run loaded by the container; the dialog does not request it itself. */
  initialPrintRun?: PrintRunRecord | null;
  /** Package for the restored run, also loaded by the container. */
  initialPackage?: any;
  onClose: () => void;
  onSave: (payload: any) => void;
  organizationId?: number;
  layoutsEndpoint?: string;
  readOnlyJobId?: boolean;
  readOnlyJobName?: boolean;
  currentUser?: any;
  existingPrintRun?: PrintRunRecord | null;
  onDeleteDraft?: DeleteDraftPrintRun;
  isDeletingDraft?: boolean;
};

const formatDateInputValue = (value: unknown): string => {
  const date = parseDisplayDate(value);
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function parseTemplateData(apiTemplate: ApiTemplate | null): Template | null {
  if (!apiTemplate) return null;

  const rawData = apiTemplate.content || apiTemplate.data;
  if (!rawData) {
    console.warn(
      '[StartPrintDialog] Template has no content or data field:',
      apiTemplate.name,
      Object.keys(apiTemplate),
    );
    return null;
  }

  if (typeof rawData === 'object') return normalizeLoadedTemplate(rawData as Template);

  try {
    let parsed = JSON.parse(rawData);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return normalizeLoadedTemplate(parsed as Template);
  } catch (err) {
    console.error('[StartPrintDialog] Failed to parse template data:', err);
    return null;
  }
}

/**
 * Build an "auto" layout that matches the template's label size (1-up)
 */
function buildAutoLayout(tpl: Template): PreviewLayout {
  const unit = tpl.realUnit || 'in';
  const dpi = tpl.dpi ?? 72;
  const labelWidth = tpl.realWidth ?? Number((tpl.width / dpi).toFixed(2));
  const labelHeight = tpl.realHeight ?? Number((tpl.height / dpi).toFixed(2));
  return {
    id: AUTO_LAYOUT_ID,
    label: 'Match label size (1-up)',
    unit,
    pageWidth: labelWidth,
    pageHeight: labelHeight,
    labelWidth,
    labelHeight,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  };
}

// A persisted label count can arrive as a string or a malformed value; only a whole positive
// count may seed the print count.
const toPrintCount = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
};

// Inventory can be fractional (for example grams), but labels are whole units.
const toPackagePrintCount = (value: unknown): number => {
  const quantity = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(quantity) && quantity > 0 ? Math.max(1, Math.floor(quantity)) : 1;
};

/**
 * Build a layout from the template's custom layout settings (if present)
 */
function buildCustomLayout(tpl: Template): PreviewLayout | null {
  if (!tpl.customLayout) return null;
  const cl = tpl.customLayout;
  return {
    id: 'custom',
    label: 'Custom Layout',
    unit: cl.unit,
    pageWidth: cl.pageWidth,
    pageHeight: cl.pageHeight,
    labelWidth: cl.labelWidth ?? tpl.realWidth ?? tpl.width / (tpl.dpi ?? 72),
    labelHeight: cl.labelHeight ?? tpl.realHeight ?? tpl.height / (tpl.dpi ?? 72),
    marginTop: cl.marginTop,
    marginRight: cl.marginRight,
    marginBottom: cl.marginBottom,
    marginLeft: cl.marginLeft,
    spacingX: cl.spacingX,
    spacingY: cl.spacingY,
    columns: cl.columns,
    rows: cl.rows,
    format: cl.format || 'sheets',
    shape: cl.shape || 'rectangle',
    labelRotation: cl.labelRotation,
    printer: 'custom',
  };
}

function getPrintRunErrorMessage(error: unknown, fallbackMessage: string, isStaged: boolean): string {
  const message = getErrorMessage(error, '').trim();

  if (isStaged) {
    const usedTagMatch = message.match(
      /^Labels for (.+?) cannot be Generated because it has been Used\.?$/i,
    );
    if (usedTagMatch) {
      return `Staged package ${usedTagMatch[1]} has already been promoted. Use the active package to print labels.`;
    }
  }

  return message || fallbackMessage;
}

function handlePrintRunError(error: unknown, fallbackMessage: string, isStaged: boolean) {
  console.error(error);
  showToastError(getPrintRunErrorMessage(error, fallbackMessage, isStaged));
}

export {
  isActiveProduct,
  getActiveProductOptions,
  getProductOptions,
  getProductOverride,
};
export { getActiveProductOverride } from './utils/productOverrides';

export default function StartPrintDialog({
  apiTemplates = EMPTY_API_TEMPLATES,
  templates = [],
  defaultJobId,
  initialValues,
  initialPrintRun = null,
  initialPackage = null,
  onClose,
  onSave,
  organizationId,
  layoutsEndpoint,
  readOnlyJobId = false,
  readOnlyJobName: _readOnlyJobName = false,
  currentUser,
  existingPrintRun: _existingPrintRun = null,
  onDeleteDraft,
  isDeletingDraft = false,
}: StartPrintDialogProps) {
  const allowArchivedProductOverrides = currentUser?.isSuperUser === true;
  const { handleSsoError } = useSsoReauth();
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const templateSelectId = useId();
  const layoutSelectId = useId();
  const templateToggleRef = useRef<HTMLButtonElement | null>(null);
  const templateSearchInputRef = useRef<HTMLInputElement | null>(null);
  const templateDropdownRef = useRef<HTMLDivElement | null>(null);
  const layoutToggleRef = useRef<HTMLButtonElement | null>(null);
  const layoutSearchInputRef = useRef<HTMLInputElement | null>(null);
  const layoutDropdownRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(1);
  const [packageMode, setPackageMode] = useState<PrintPackageMode>('source');
  const packagedDateEditedRef = useRef(false);
  const [_qrDataUrl, setQrDataUrl] = useState('');

  const [templateSearch, setTemplateSearch] = useState('');
  const [templateSearchDebounced, setTemplateSearchDebounced] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | string | null>(
    initialValues?.templateId ?? null,
  );
  const [retainedSelectedTemplate, setRetainedSelectedTemplate] = useState<TemplateRecord | null>(null);
  const templateSelectionDirtyRef = useRef(false);
  const layoutSelectionDirtyRef = useRef(false);
  const {
    data: effectiveApiTemplates,
    hasMore: hasMoreTemplates,
    isLoading: isLoadingTemplates,
    isSearching: isSearchingTemplates,
    loadMore: loadMoreTemplates,
  } = useAvailablePrintTemplates({
    organizationId,
    apiTemplates,
    search: templateSearchDebounced,
    selectedTemplate: retainedSelectedTemplate,
    selectedTemplateId,
  });

  useEffect(() => {
    templateSelectionDirtyRef.current = false;
    layoutSelectionDirtyRef.current = false;
  }, [initialValues?.printRunId, organizationId]);

  useEffect(() => {
    if (templateSelectionDirtyRef.current) return;
    setRetainedSelectedTemplate(null);
    setSelectedTemplateId(initialValues?.templateId ?? null);
  }, [initialValues?.templateId, initialValues?.printRunId, organizationId]);

  const templateOptions = useMemo<TemplateRecord[]>(() => {
    if (effectiveApiTemplates.length > 0) {
      return effectiveApiTemplates;
    }
    return [...templates]
      .sort((left, right) =>
        (left || '').localeCompare(right || '', undefined, { sensitivity: 'base' }),
      )
      .map((name) => ({ id: name, name }));
  }, [effectiveApiTemplates, templates]);

  const packagedDate = initialValues?.packagedDate
    ? formatDateInputValue(initialValues.packagedDate)
    : '';

  let printedDate = null;
  if (initialValues?.printedDate) {
    printedDate = new Date(initialValues?.printedDate).toLocaleDateString();
  } else {
    printedDate = new Date().toLocaleDateString();
  }

  const initialPackageLabel = initialValues?.packageLabel || initialValues?.packageTag || '';

  const [form, setForm] = useState({
    name: initialValues?.name || '',
    jobId: initialValues?.jobId || defaultJobId || ' ',
    printRunId: initialValues?.printRunId,
    template: templateOptions[0]?.name || '',
    packageId: initialValues?.packageId != null ? String(initialValues.packageId) : '',
    productName: initialValues?.productName || '',
    productNameCustom: '',
    productOverrideId: null as number | null,
    packagedDate: packagedDate,
    packageTag: initialPackageLabel,
    count: initialValues?.count ?? 1,
    printedDate: printedDate,
    isStaged: initialValues?.isStaged,
    splitIntoReels: false,
    labelsPerReel: 100,
  });
  const [useCustomProduct, setUseCustomProduct] = useState(false);
  const [isProductOverridden, setIsProductOverridden] = useState(false);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [existingQrValues, setExistingQrValues] = useState<string[] | null>(null);
  const hasInitialPrintContext = Boolean(
    initialValues?.printRunId || initialValues?.packageId || initialPackageLabel,
  );
  const isExistingDraft = initialValues?.status === 4 || initialValues?.status === 'Draft';
  const [isLoadingInitialPrintContext, setIsLoadingInitialPrintContext] = useState(
    hasInitialPrintContext,
  );
  const { close, isClosing } = useAnimatedClose(onClose);

  useEffect(() => {
    if (selectedTemplateId != null && effectiveApiTemplates.length > 0) {
      const matchingTemplate = effectiveApiTemplates.find(
        (t) => String(t.id) === String(selectedTemplateId),
      );
      if (matchingTemplate && form.template !== matchingTemplate.name) {
        setForm((prev) => ({ ...prev, template: matchingTemplate.name }));
      }
    }
  }, [effectiveApiTemplates, form.template, selectedTemplateId]);

  //auto select first template when templates become available
  useEffect(() => {
    if (initialValues?.templateId) return;
    if (selectedTemplateId == null && templateOptions.length > 0) {
      const firstTemplate = templateOptions[0];
      setSelectedTemplateId(firstTemplate.id);
      setForm((prev) => ({ ...prev, template: firstTemplate.name }));
    }
  }, [templateOptions, initialValues?.templateId, selectedTemplateId]);

  // Fetch package and/or printRun by ID/label when passed via initialValues, populate count and add to list
  useEffect(() => {
    if (!hasInitialPrintContext) {
      setIsLoadingInitialPrintContext(false);
      return;
    }
    if (!organizationId) {
      setIsLoadingInitialPrintContext(true);
      return;
    }

    let cancelled = false;
    setIsLoadingInitialPrintContext(true);

    (async () => {
      try {
        const existingPrintRun = initialValues.printRunId ? initialPrintRun : null;
        if (existingPrintRun) {
          if (existingPrintRun?.labelTemplateData && !templateSelectionDirtyRef.current) {
            try {
              const labelTemplateData = typeof existingPrintRun.labelTemplateData === 'string'
                ? JSON.parse(existingPrintRun.labelTemplateData)
                : existingPrintRun.labelTemplateData;
              setSavedTemplateSnapshot(rebindStagedTacAdjustment(labelTemplateData as Template, organizationId, existingPrintRun.stagePackageCoaId));
              const restoredTemplateId = existingPrintRun.templateId;
              if (restoredTemplateId != null) {
                setRetainedSelectedTemplate(null);
                setSelectedTemplateId(restoredTemplateId);
              }
              if (initialValues.printRunId && !isExistingDraft) {
                const confirmedPackagedDate = labelTemplateData?.confirmedDates?.find(
                  (date) => date.kind === 'packagedDate',
                );
                const packagedVariable = labelTemplateData?.themeVariables?.find(
                  (variable) => variable.dateKind === 'packagedDate',
                );
                const snapshotPackagedDate = confirmedPackagedDate
                  ? confirmedPackagedDate.confirmedDate || ''
                  : packagedVariable
                    ? String(packagedVariable.value ?? '')
                    : null;
                if (snapshotPackagedDate != null) {
                  setForm((previous) => ({ ...previous, packagedDate: snapshotPackagedDate }));
                }
              }
              if (labelTemplateData?.themeVariables?.length > 0) {
                const themeOverrides = {};
                const restoredDateStates = {};
                for (const themeVar of labelTemplateData.themeVariables) {
                  themeOverrides[themeVar.key] = themeVar.value;
                  if (themeVar.dateKind && themeVar.dateState) {
                    restoredDateStates[themeVar.key] = themeVar.dateState;
                  }
                }
                setThemeOverrides(themeOverrides);
                setDateStates(restoredDateStates);
              }
              if (labelTemplateData?.selectedLayoutId && !layoutSelectionDirtyRef.current) {
                setSelectedLayoutId(labelTemplateData.selectedLayoutId);
              }
              if (labelTemplateData?.topOffset != null) setTopOffset(labelTemplateData.topOffset);
              if (labelTemplateData?.leftOffset != null) setLeftOffset(labelTemplateData.leftOffset);
              if (labelTemplateData?.pageRotation != null)
                setPageRotation(labelTemplateData.pageRotation);
              const draftPrintSettings = labelTemplateData?.draftPrintSettings;
              if (draftPrintSettings && typeof draftPrintSettings === 'object') {
                if (draftPrintSettings.outputMode === 'labels+csv' || draftPrintSettings.outputMode === 'csv') {
                  setOutputMode(draftPrintSettings.outputMode);
                }
                const storedLabelsPerReel = Number(draftPrintSettings.labelsPerReel);
                setForm((prev) => ({
                  ...prev,
                  splitIntoReels: draftPrintSettings.splitIntoReels === true,
                  labelsPerReel: Number.isFinite(storedLabelsPerReel) && storedLabelsPerReel > 0
                    ? storedLabelsPerReel
                    : prev.labelsPerReel,
                }));
              }
            } catch (err) {
              console.warn('[StartPrintDialog] Failed to restore saved draft settings:', err);
            }
          }
          if (
            existingPrintRun.qrs &&
            Array.isArray(existingPrintRun.qrs) &&
            existingPrintRun.qrs.length > 0
          ) {
            setExistingQrValues(existingPrintRun.qrs);
          }
        }

        const pkg = initialPackage;
        const {
          packageLabel: resolvedPackageTag,
          packageId: resolvedLookupPackageId,
          hasPackageId: hasPackageIdForLookup,
          isStaged: isStagedPackage,
        } = resolveRestoredPackageLookup({
          printRun: existingPrintRun,
          packageId: initialValues.packageId,
          packageLabel: initialPackageLabel,
          isStaged: initialValues.isStaged,
        });
        const packageIdForLookup = resolvedLookupPackageId;
        if (cancelled) return;

        const resolvedPackageId = hasPackageIdForLookup ? packageIdForLookup : (pkg?.id ?? '');
        const resolvedPackageLabel = resolvedPackageTag || pkg?.label || '';
        const parsedOverrideId = existingPrintRun?.productOverride == null
          ? null
          : Number(existingPrintRun.productOverride);
        const productOverrideId = parsedOverrideId != null
          && Number.isSafeInteger(parsedOverrideId)
          && parsedOverrideId > 0
          ? parsedOverrideId
          : null;
        const resolvedProductName = productOverrideId
          ? existingPrintRun?.productOverrideName
            || productOptions.find((p) => Number(p.id) === productOverrideId)?.name
            || ''
          : pkg?.productName || '';
        const quantity = pkg?.quantity;
        const packagePackagedDate = formatDateInputValue(pkg?.packagedDate);
        setForm((prev) => ({
          ...prev,
          printRunId: Number.isSafeInteger(Number(existingPrintRun?.id)) && Number(existingPrintRun?.id) > 0
            ? Number(existingPrintRun?.id) : prev.printRunId,
          name: existingPrintRun?.jobName || prev.name,
          packageId: resolvedPackageId ? String(resolvedPackageId) : '',
          packageTag: resolvedPackageLabel,
          productName: resolvedProductName || prev.productName,
          productOverrideId,
          count: countManuallyEditedRef.current
            ? prev.count
            : existingPrintRun?.count ?? (
              initialValues?.printRunId != null || prev.printRunId != null
                ? prev.count
                : toPackagePrintCount(quantity)
            ),
          jobId: existingPrintRun?.jobId ? String(existingPrintRun.jobId) : prev.jobId,
          template: !templateSelectionDirtyRef.current
            ? existingPrintRun?.templateName || prev.template
            : prev.template,
          isStaged: isStagedPackage,
          printedDate: existingPrintRun?.printedDate ?? new Date().toLocaleDateString(),
          packagedDate: initialValues.printRunId == null && packagePackagedDate
            ? packagePackagedDate
            : prev.packagedDate,
        }));
        if (resolvedPackageLabel) {
          setPackageSearch(resolvedPackageLabel);
        }
        if (productOverrideId) {
          setIsProductOverridden(true);
        }

      } catch (err) {
        console.error('[StartPrintDialog] Failed to fetch package by ID:', err);
      } finally {
        if (!cancelled) {
          setIsLoadingInitialPrintContext(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    initialValues?.packageId,
    initialValues?.printRunId,
    initialValues?.isStaged,
    initialValues?.stagedPackage,
    initialPrintRun,
    initialPackage,
    initialPackageLabel,
    hasInitialPrintContext,
    organizationId,
    isExistingDraft,
  ]);

  // Base layouts from the API, with endpoint/default fallback (without auto layout)
  const baseLayouts = usePreviewLayouts(layoutsEndpoint);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>(
    defaultPreviewLayouts[0]?.id || '',
  );
  const lastApiTemplateIdRef = useRef<string | null>(null);
  const countManuallyEditedRef = useRef(false);
  const [pageRotation, setPageRotation] = useState<0 | 90 | 180 | 270>(0);
  const [topOffset, setTopOffset] = useState(0);
  const [leftOffset, setLeftOffset] = useState(0);

  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [renderedPreviewContextToken, setRenderedPreviewContextToken] = useState<object | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [legacyImages, setLegacyImages] = useState<Record<string, string>>({});
  const [legacyFonts, setLegacyFonts] = useState<Record<string, string>>({});
  const [newAssetImages, setNewAssetImages] = useState<Record<string, string>>({});
  const [newAssetFonts, setNewAssetFonts] = useState<Record<string, string>>({});
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isLoadingNewAssets, setIsLoadingNewAssets] = useState(false);

  const [packageSearch, setPackageSearch] = useState('');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(-1);
  const [layoutSearch, setLayoutSearch] = useState('');
  const [isLayoutDropdownOpen, setIsLayoutDropdownOpen] = useState(false);
  const [activeLayoutIndex, setActiveLayoutIndex] = useState(-1);
  const latestMovingMouseEvent = useRef<MouseEvent | null>(null);

  useEffect(() => {
    let position: { x: number; y: number } | undefined;
    const trackMouse = (event: MouseEvent) => {
      const { clientX: x, clientY: y } = event;
      const moved = position?.x !== x || position?.y !== y;
      position = { x, y };
      latestMovingMouseEvent.current = event.type === 'mousemove' && moved ? event : null;
    };
    // Capture before option handlers and keep tracking when the pickers are closed
    // or the pointer is between options. Scroll can emit mousemove at the same point.
    document.addEventListener('mousemove', trackMouse, true);
    document.addEventListener('mousedown', trackMouse, true);
    return () => {
      document.removeEventListener('mousemove', trackMouse, true);
      document.removeEventListener('mousedown', trackMouse, true);
      latestMovingMouseEvent.current = null;
    };
  }, []);
  const [showProductOverrideModal, setShowProductOverrideModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [isReassignMode, setIsReassignMode] = useState(false);
  const { isReassignSaving, reassign, reassignIssued } = useReassignPrintRun();
  const [reassignConfirmPending, setReassignConfirmPending] = useState(false);
  const savedProductStateRef = useRef<{
    productOverrideId: number | null;
    productName: string;
    productNameCustom: string;
    isProductOverridden: boolean;
    useCustomProduct: boolean;
  } | null>(null);

  // The picker list is active-only, so a persisted override that was later archived is fetched by id.
  const {
    products,
    selectedOverrideProduct: resolvedOverrideProduct,
    selectedListOverrideProduct,
    historicalOverrideLookupFailed,
    isLoadingHistoricalOverride,
    isIncomplete: isProductCatalogIncomplete,
    isLoading: isLoadingProductCatalog,
    retry: retryProductCatalog,
  } = useProductCatalog({
    organizationId,
    productOverrideId: form.productOverrideId,
    allowArchivedProductOverrides,
  });

  useEffect(() => {
    if (!isProductCatalogIncomplete) return;
    showToastError('Unable to load all products. Some products may be unavailable.');
  }, [isProductCatalogIncomplete]);

  const [packageSearchDebounced, setPackageSearchDebounced] = useState('');
  const canSearchPackages = Boolean(
    organizationId
    && packageSearchDebounced.trim().length >= 2
    && !initialValues?.printRunId
    && !initialValues?.isStaged
    && !form.isStaged,
  );
  const {
    packages: packageCoaOptions,
    isLoading: isLoadingPackages,
    error: packageSearchError,
    retry: retryPackageSearch,
  } = usePrintPackageSearch({
    organizationId,
    search: packageSearchDebounced.trim(),
    enabled: canSearchPackages,
    initialPackage,
    initialPackageLabel,
    products,
  });
  const packageSelectionContext = JSON.stringify([organizationId ?? null, initialValues?.printRunId ?? null]);
  const [retainedSelectedPackage, setRetainedSelectedPackage] = useState<{
    context: string;
    option: PackageCoaOption;
  } | null>(null);
  useEffect(() => {
    setRetainedSelectedPackage(null);
  }, [packageSelectionContext]);
  const [selectedPackageCoa, setSelectedPackageCoa] = useState<Record<string, any> | null>(null);
  const stagedPackageCoaIdRef = useRef<number | null>(null);
  const [isStagedCoaDataInvalid, setIsStagedCoaDataInvalid] = useState(false);
  const [isLoadingCanonicalCoa, setIsLoadingCoa] = useState(() => hasInitialPrintContext);
  // Prevent repeated api calls for the same package coa between renders
  const packageCoaCacheRef = useRef<Record<string, Record<string, any>>>({});

  const [themeOverrides, setThemeOverrides] = useState<Record<string, string | number | boolean>>(
    {},
  );
  const [dateStates, setDateStates] = useState<Record<string, DateVariableState>>({});
  const [savedTemplateSnapshot, setSavedTemplateSnapshot] = useState<Template | null>(null);
  const [recalculateRestoredDates, setRecalculateRestoredDates] = useState(false);
  const automaticDateInputsRef = useRef<{
    packagedDate: string;
    shelfLifeDays?: number;
    sourceSignature: string;
  } | null>(null);

  const [completedPrintRun, setCompletedPrintRun] = useState<{
    organizationId: number;
    id: number | string;
  } | null>(null);
  const [deferredSavePayload, setDeferredSavePayload] = useState<any>(null);
  const pendingPrint = usePendingPrintRequest(organizationId);
  const acceptedSavedId = completedPrintRun?.id ?? (initialValues?.printRunId ? initialPrintRun?.id : undefined);
  useEffect(() => {
    if (acceptedSavedId && pendingPrint.intent?.completedPrintRunId === Number(acceptedSavedId)) {
      void pendingPrint.accept(acceptedSavedId);
    }
  }, [acceptedSavedId, pendingPrint.intent?.completedPrintRunId, pendingPrint.accept]);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [outputMode, setOutputMode] = useState<DraftOutputMode>('labels+csv');
  const [generatedPdfs, setGeneratedPdfs] = useState<
    Array<{
      key: string;
      label: string;
      filename: string;
      rangeStart: number;
      rangeEnd: number;
    }>
  >([]);
  const ownedPdfKeysRef = useRef(new Set<string>());
  const [generatedOutput, setGeneratedOutput] = useState<{
    blob: Blob;
    url: string;
    labelCount: number;
    extra?: Record<string, unknown>;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // A retained intent protects admission even while this page owns a healthy
  // request. Recovery actions belong to a waiting/interrupted request, not the
  // normal record-validation and file-generation steps.
  const hasAcceptedSavedPrint = Boolean(acceptedSavedId
    && pendingPrint.intent?.completedPrintRunId === Number(acceptedSavedId));
  const showPrintRecovery = (!isSaving && pendingPrint.blocked
    && (!hasAcceptedSavedPrint || Boolean(pendingPrint.error)))
    || (isSaving && pendingPrint.waiting
      && !pendingPrint.intent?.completedPrintRunId);
  const printSubmissionRef = useRef(false);
  const [isPrintStarted, setIsPrintStarted] = useState(false);
  const [isDraft, setIsDraft] = useState(
    () => isExistingDraft,
  );
  const [isDraftSaved, setIsDraftSaved] = useState(
    () => isExistingDraft,
  );
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [savedDraftRun, setSavedDraftRun] = useState<PrintRunRecord | null>(null);
  const [savedDraftHasSelection, setSavedDraftHasSelection] = useState(false);
  // A successful response without identity may have committed a capture. Freeze
  // this editor until the saved draft can be reopened instead of issuing current data.
  const [unresolvedDraftSaveContext, setUnresolvedDraftSaveContext] = useState<string | null>(null);
  const hasUnresolvedDraftSave = unresolvedDraftSaveContext === packageSelectionContext;
  useEffect(() => {
    setSavedDraftRun(null);
    setSavedDraftHasSelection(false);
    setUnresolvedDraftSaveContext(null);
  }, [initialValues?.printRunId, organizationId]);
  const draftRun = savedDraftRun ?? initialPrintRun;
  // Keep the capture while the draft transitions to a print, too.
  const hasSavedDraftPackage = Boolean(form.printRunId && (isDraft || isExistingDraft || savedDraftRun?.status === 4 || savedDraftRun?.status === 'Draft')
    && (savedDraftHasSelection || draftRun?.packageId != null || draftRun?.stagePackageCoaId != null
      || initialValues?.packageId != null || initialValues?.isStaged));
  const isPackageSelectionLocked = isDraftSaving || hasUnresolvedDraftSave || hasSavedDraftPackage
    || Boolean(initialValues?.printRunId && !isDraft);
  const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false);
  const arePrintInputsDisabled = isPrintStarted || (!!initialValues?.printRunId && !isDraft);
  const [workerCount, setWorkerCount] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('pdf-worker-count');
      if (stored) {
        const n = parseInt(stored, 10);
        if (n >= 1 && n <= 8) return n;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_WORKER_COUNT;
  });
  const [reelProgress, setReelProgress] = useState<{
    completed: number;
    total: number;
    inFlight: number[];
  } | null>(null);
  const [savingPhase, setSavingPhase] = useState<'idle' | 'reels' | 'finalizing'>('idle');
  const [reelPageStatus, setReelPageStatus] = useState<{
    totalReels: number;
    totalPages: number;
    pageCounts: number[];
    completedPages: number;
    completedReels: number[];
  } | null>(null);
  const [qrGenerationProgress, setQrGenerationProgress] = useState<{
    generated: number;
    total: number;
  } | null>(null);
  const [displayedQrGenerated, setDisplayedQrGenerated] = useState(0);
  const [singlePdfMiniReelKeys, setSinglePdfMiniReelKeys] = useState<string[] | null>(null);
  const [isMiniReelMode, setIsMiniReelMode] = useState(false);
  const [mergeProgress, setMergeProgress] = useState<{
    phase: 'merging' | 'serializing' | 'writing';
    merged: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (!qrGenerationProgress) {
      setDisplayedQrGenerated(0);
      return;
    }

    const target = qrGenerationProgress.generated;
    const interval = window.setInterval(() => {
      setDisplayedQrGenerated((prev) => {
        if (prev >= target) return prev;
        const delta = target - prev;
        const step = Math.max(1, Math.ceil(delta * 0.14));
        return Math.min(target, prev + step);
      });
    }, 40);

    return () => window.clearInterval(interval);
  }, [qrGenerationProgress?.generated, qrGenerationProgress?.total]);

  const getToken = useCallback(() => window.localStorage.getItem(AUTH_STORAGE_KEY), []);

  const assetApi = useMemo<AssetApi>(
    () => ({
      getFileId: ({ file, fileType, mimeType, organizationId: targetOrganizationId }) =>
        uploadFileId({
          file,
          fileType,
          mimeType,
          organizationId: targetOrganizationId,
        }),
      getStaticFiles,
      getFileLink,
      getFileContentUrl,
    }),
    [],
  );

  //legacy asset resolver
  const legacyAssetResolver = useMemo<LegacyAssetResolver | null>(() => {
    if (!organizationId) return null;
    return createLegacyAssetResolver({
      organizationId: String(organizationId),
      apiBaseUrl: DEFAULT_API_BASE_URL,
      getToken,
    });
  }, [organizationId, getToken]);

  const selectedApiTemplate = useMemo(() => {
    if (selectedTemplateId != null) {
      return effectiveApiTemplates.find(
        (template) => String(template.id) === String(selectedTemplateId),
      ) || null;
    }
    return effectiveApiTemplates.find((template) => template.name === form.template) || null;
  }, [effectiveApiTemplates, form.template, selectedTemplateId]);

  const selectedApiTemplateKey = selectedApiTemplate == null
    ? null
    : String(selectedApiTemplate.id);

  const currentSelectedTemplate = useMemo<Template | null>(() => {
    return parseTemplateData(selectedApiTemplate as ApiTemplate | null);
  }, [selectedApiTemplate]);
  const baseSelectedTemplate = form.printRunId && savedTemplateSnapshot
    ? savedTemplateSnapshot
    : currentSelectedTemplate;
  const tacContextKey = JSON.stringify([selectedApiTemplateKey, form.packageId, form.isStaged, selectedPackageCoa?.coaId]);
  const [tacEdits, setTacEdits] = useState<{ key: string; packageKey: string; adjustment: TacAdjustment } | null>(null);
  const [tacAcknowledgment, setTacAcknowledgment] = useState<string | null>(null);
  const [showTacGate, setShowTacGate] = useState(false);
  const selectedTemplate = useMemo(() => baseSelectedTemplate && tacEdits?.key === tacContextKey
    ? { ...baseSelectedTemplate, tacAdjustments: { ...baseSelectedTemplate.tacAdjustments, [tacEdits.packageKey]: tacEdits.adjustment } }
    : baseSelectedTemplate, [baseSelectedTemplate, tacEdits, tacContextKey]);
  const effectiveDateTemplate = selectedTemplate;
  const usedDateVariables = useMemo(
    () => getUsedPrintDateVariables(effectiveDateTemplate),
    [effectiveDateTemplate],
  );
  const packagedDateVariable = usedDateVariables.find((variable) => variable.dateKind === 'packagedDate');
  const displayedThemeVariables = effectiveDateTemplate?.themeVariables || selectedTemplate?.themeVariables || [];

  const selectedFormatDef = useMemo(() => {
    return getFormatOrDefault(selectedTemplate?.outputFormat);
  }, [selectedTemplate?.outputFormat]);

  const selectedFormatCaps = useMemo(() => {
    return selectedFormatDef.capabilities;
  }, [selectedFormatDef]);

  // Preserve the existing unit-allocation rules: single PDF output creates new
  // units; CSV, ZPL and existing reel output reuse their issued unit codes.
  const outputPlan = useMemo(() => {
    const totalCount = Number(form.count) || 1;
    const labelsPerReel = form.splitIntoReels ? Number(form.labelsPerReel) : totalCount;
    const reelCount = Math.ceil(totalCount / labelsPerReel);
    const useUserReels = form.splitIntoReels && reelCount > 1;
    const useMiniReels = selectedFormatCaps.supportsReelSplitting
      && !useUserReels && shouldUseMiniReels(totalCount) && isWorkerSupported();
    const createsPdfPrintRun = outputMode !== 'csv'
      && selectedFormatCaps.supportsReelSplitting && !useUserReels && !useMiniReels;
    return { totalCount, labelsPerReel, reelCount, useUserReels, useMiniReels, createsPdfPrintRun };
  }, [form.count, form.splitIntoReels, form.labelsPerReel, selectedFormatCaps.supportsReelSplitting, outputMode]);
  const reuseIssuedCoa = Boolean(initialValues?.printRunId && !isDraft && !hasSavedDraftPackage && !form.isStaged
    && !outputPlan.createsPdfPrintRun);
  const issuedPrintCoa = useIssuedPrintCoa(initialPrintRun, initialValues?.printRunId, form.packageId);
  const reuseIssuedStagedCoa = Boolean(initialValues?.printRunId && !isDraft && !hasSavedDraftPackage
    && form.isStaged && !outputPlan.createsPdfPrintRun);
  const issuedStagedPrintCoa = useIssuedStagedPrintCoa(initialPrintRun, initialValues?.printRunId);
  const reuseDraftCoa = Boolean(hasSavedDraftPackage && !form.isStaged);
  const capturedDraftCoa = useDraftPrintCoa(draftRun, form.printRunId, form.packageId);
  const draftCoaUnavailable = reuseDraftCoa && capturedDraftCoa == null;
  const isDraftOutputBlocked = draftCoaUnavailable || hasUnresolvedDraftSave;
  const draftOutputBlockedRef = useRef(isDraftOutputBlocked);
  draftOutputBlockedRef.current = isDraftOutputBlocked;
  const effectivePackageCoa = reuseDraftCoa ? capturedDraftCoa
    : reuseIssuedCoa ? issuedPrintCoa
      : reuseIssuedStagedCoa ? issuedStagedPrintCoa : selectedPackageCoa;
  // Archived snapshots remain valid rendering context without a live COA ID.
  const hasCoaForOutput = reuseDraftCoa ? capturedDraftCoa != null
    : reuseIssuedCoa ? issuedPrintCoa != null
      : reuseIssuedStagedCoa ? issuedStagedPrintCoa != null : Boolean(selectedPackageCoa?.coaId);
  const isLoadingCoa = reuseDraftCoa || reuseIssuedStagedCoa ? false
    : reuseIssuedCoa ? issuedPrintCoa == null : isLoadingCanonicalCoa;

  const effectivePageRotation = selectedFormatCaps.supportsPageLayout
    ? pageRotation
    : 0;

  const availableLayouts = useMemo<PreviewLayout[]>(() => {
    if (selectedTemplate) {
      const layouts = [buildAutoLayout(selectedTemplate), ...baseLayouts];
      const customLayout = buildCustomLayout(selectedTemplate);
      if (customLayout) {
        layouts.push(customLayout);
      }
      return layouts;
    }
    return baseLayouts;
  }, [selectedTemplate, baseLayouts]);

  // When template changes, use its defaultLayoutId if it exists
  useEffect(() => {
    if (!selectedTemplate) return;
    //prevent override of layout when editing/reprinting as we get this from print run
    if (initialValues?.printRunId && !isDraft) return;

    // A restored template finishing its by-ID lookup is not a new selection.
    // Preserve a layout edited while that same template was still hydrating.
    const layoutTemplateKey = selectedTemplateId != null
      ? String(selectedTemplateId)
      : selectedApiTemplateKey;
    const templateChanged = lastApiTemplateIdRef.current !== layoutTemplateKey;
    lastApiTemplateIdRef.current = layoutTemplateKey;

    const desiredLayoutId =
      selectedTemplate.defaultLayoutId ||
      (selectedTemplate.customLayout ? 'custom' : AUTO_LAYOUT_ID);
    const layoutIds = availableLayouts.map((l) => l.id);
    const hasSelectedLayout = !!selectedLayoutId && layoutIds.includes(selectedLayoutId);
    const savedDraftLayoutId = isDraft
      && initialValues?.printRunId
      && !layoutSelectionDirtyRef.current
      ? savedTemplateSnapshot?.selectedLayoutId
      : null;
    const hasSavedDraftLayout = !!savedDraftLayoutId
      && (savedDraftLayoutId === AUTO_LAYOUT_ID || layoutIds.includes(savedDraftLayoutId));

    if (hasSavedDraftLayout) {
      if (selectedLayoutId !== savedDraftLayoutId) {
        setSelectedLayoutId(savedDraftLayoutId);
      }
      return;
    }

    if (layoutSelectionDirtyRef.current && !templateChanged && hasSelectedLayout) {
      return;
    }

    // Check if desired layout exists in available layouts
    const desiredExists = desiredLayoutId === AUTO_LAYOUT_ID || layoutIds.includes(desiredLayoutId);

    if (desiredExists && selectedLayoutId !== desiredLayoutId) {
      setSelectedLayoutId(desiredLayoutId);
    } else if ((templateChanged || !hasSelectedLayout) && layoutIds.length > 0) {
      // Fallback to first available layout
      setSelectedLayoutId(layoutIds[0]);
    }
  }, [
    selectedTemplateId,
    selectedApiTemplateKey,
    selectedTemplate?.defaultLayoutId,
    selectedTemplate?.customLayout,
    availableLayouts,
    initialValues?.printRunId,
    isDraft,
    savedTemplateSnapshot?.selectedLayoutId,
    selectedLayoutId,
  ]);

  // A package has no "labels wanted" field, so the template's saved Total labels is the count
  // intent and outranks the package quantity. A restored run and an operator edit both outrank it.
  useEffect(() => {
    if (initialValues?.printRunId != null || form.printRunId != null) return;
    if (countManuallyEditedRef.current) return;

    const templateLabelCount = toPrintCount(selectedTemplate?.defaultLabelCount);
    if (templateLabelCount === undefined) return;

    setForm((prev) => (
      Number(prev.count) === templateLabelCount ? prev : { ...prev, count: templateLabelCount }
    ));
  }, [
    selectedApiTemplateKey,
    selectedTemplate?.defaultLabelCount,
    initialValues?.printRunId,
    form.printRunId,
    form.count,
    form.packageId,
  ]);

  useEffect(() => {
    if (initialValues?.printRunId) return;
    setThemeOverrides({});
    setDateStates({});
    automaticDateInputsRef.current = null;
  }, [selectedApiTemplateKey, initialValues?.printRunId]);

  useEffect(() => {
    if (!selectedTemplate) return;
    if (initialValues?.printRunId && !isDraft) return;
    setTopOffset(selectedTemplate.topOffset ?? 0);
    setLeftOffset(selectedTemplate.leftOffset ?? 0);
    setPageRotation(
      selectedFormatCaps.supportsPageLayout
        ? selectedTemplate.pageRotation ?? 0
        : 0,
    );
  }, [selectedApiTemplateKey, isDraft, selectedFormatCaps.supportsPageLayout]);

  const selectedLayout = useMemo(() => {
    return availableLayouts.find((l) => l.id === selectedLayoutId) || availableLayouts[0] || null;
  }, [availableLayouts, selectedLayoutId]);

  const sortedAvailableLayouts = useMemo(() => {
    return [...availableLayouts].sort((a, b) => {
      const left = a.label || a.id || '';
      const right = b.label || b.id || '';
      return left.localeCompare(right, undefined, { sensitivity: 'base' });
    });
  }, [availableLayouts]);

  const filteredAvailableLayouts = useMemo(() => {
    const term = layoutSearch.trim().toLowerCase();
    if (!term) return layoutSearch ? [] : sortedAvailableLayouts;

    return sortedAvailableLayouts.filter((layout) => {
      const label = (layout.label || '').toLowerCase();
      const id = (layout.id || '').toLowerCase();
      return label.includes(term) || id.includes(term);
    });
  }, [sortedAvailableLayouts, layoutSearch]);

  const sortedTemplateOptions = useMemo(() => {
    return [...templateOptions].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
    );
  }, [templateOptions]);

  const filteredTemplateOptions = useMemo(() => {
    const term = templateSearch.trim().toLowerCase();
    if (!term) return templateSearch ? [] : sortedTemplateOptions;

    return sortedTemplateOptions.filter((template) =>
      (template.name || '').toLowerCase().includes(term),
    );
  }, [sortedTemplateOptions, templateSearch]);

  const closeTemplatePicker = useCallback((restoreFocus = false) => {
    setIsTemplateDropdownOpen(false);
    setTemplateSearch('');
    setTemplateSearchDebounced('');
    if (restoreFocus) {
      window.setTimeout(() => templateToggleRef.current?.focus(), 0);
    }
  }, []);

  const selectTemplate = useCallback((template: TemplateRecord) => {
    const templateName = template.name;
    const templateData = parseTemplateData(template as ApiTemplate | null);
    const templateChanged = String(template.id) !== selectedApiTemplateKey;
    if (templateChanged) {
      layoutSelectionDirtyRef.current = false;
    }
    if (
      isDraft
      && (form.printRunId || initialValues?.printRunId)
      && templateChanged
    ) {
      templateSelectionDirtyRef.current = true;
      setSavedTemplateSnapshot(null);
      setThemeOverrides({});
      setDateStates({});
      automaticDateInputsRef.current = null;
      setRecalculateRestoredDates(false);
      setIsDraftSaved(false);
    }
    setRetainedSelectedTemplate(template || null);
    setSelectedTemplateId(template?.id ?? null);
    if (templateChanged) {
      setSelectedLayoutId(
        templateData?.defaultLayoutId
        || (templateData?.customLayout ? 'custom' : AUTO_LAYOUT_ID),
      );
    }
    setForm((current) => ({ ...current, template: templateName }));
    closeTemplatePicker(true);
  }, [
    closeTemplatePicker,
    form.printRunId,
    initialValues?.printRunId,
    isDraft,
    selectedApiTemplateKey,
  ]);

  const closeLayoutPicker = useCallback((restoreFocus = false) => {
    setIsLayoutDropdownOpen(false);
    setLayoutSearch('');
    if (restoreFocus) {
      window.setTimeout(() => layoutToggleRef.current?.focus(), 0);
    }
  }, []);

  const selectLayout = useCallback((layoutId: string) => {
    if (layoutId === selectedLayoutId) {
      closeLayoutPicker(true);
      return;
    }
    layoutSelectionDirtyRef.current = true;
    if (isDraft && (form.printRunId || initialValues?.printRunId)) {
      setIsDraftSaved(false);
    }
    setSelectedLayoutId(layoutId);
    closeLayoutPicker(true);
  }, [closeLayoutPicker, form.printRunId, initialValues?.printRunId, isDraft, selectedLayoutId]);

  useEffect(() => {
    if (!isTemplateDropdownOpen || filteredTemplateOptions.length === 0) {
      setActiveTemplateIndex(-1);
      return;
    }

    const selectedIndex = filteredTemplateOptions.findIndex(
      (option) => String(option.id) === String(selectedTemplateId),
    );
    setActiveTemplateIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [filteredTemplateOptions, isTemplateDropdownOpen, selectedTemplateId]);

  useEffect(() => {
    if (!isLayoutDropdownOpen || filteredAvailableLayouts.length === 0) {
      setActiveLayoutIndex(-1);
      return;
    }

    const selectedIndex = filteredAvailableLayouts.findIndex(
      (option) => option.id === selectedLayoutId,
    );
    setActiveLayoutIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [filteredAvailableLayouts, isLayoutDropdownOpen, selectedLayoutId]);

  useEffect(() => {
    if (isTemplateDropdownOpen) {
      scrollActivePickerOptionIntoView(templateSelectId, activeTemplateIndex);
    }
  }, [activeTemplateIndex, filteredTemplateOptions, isTemplateDropdownOpen, templateSelectId]);

  useEffect(() => {
    if (isLayoutDropdownOpen) {
      scrollActivePickerOptionIntoView(layoutSelectId, activeLayoutIndex);
    }
  }, [activeLayoutIndex, filteredAvailableLayouts, isLayoutDropdownOpen, layoutSelectId]);

  useEffect(() => {
    if (!isTemplateDropdownOpen) {
      setTemplateSearch('');
      return;
    }

    const focusTimer = window.setTimeout(() => {
      templateSearchInputRef.current?.focus();
      templateSearchInputRef.current?.select();
    }, 0);

    const handlePointerDown = (event: MouseEvent) => {
      if (!templateDropdownRef.current?.contains(event.target as Node)) {
        closeTemplatePicker();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isComposingKeyEvent(event)) {
        closeTemplatePicker(true);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeTemplatePicker, isTemplateDropdownOpen]);

  useEffect(() => {
    if (!isLayoutDropdownOpen) {
      setLayoutSearch('');
      return;
    }

    const focusTimer = window.setTimeout(() => {
      layoutSearchInputRef.current?.focus();
      layoutSearchInputRef.current?.select();
    }, 0);

    const handlePointerDown = (event: MouseEvent) => {
      if (!layoutDropdownRef.current?.contains(event.target as Node)) {
        closeLayoutPicker();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isComposingKeyEvent(event)) {
        closeLayoutPicker(true);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeLayoutPicker, isLayoutDropdownOpen]);

  const selectedPackage = useMemo(() => {
    const matchesSelection = (pkg: PackageCoaOption) => {
      if (form.packageId) return pkg.id === form.packageId;
      return Boolean(
        form.packageTag &&
        (pkg.label === form.packageTag || pkg.coaData?.packageTag === form.packageTag),
      );
    };
    // Search results are transient. A debounce, empty result or pending request
    // must not remove the package metadata selected for this editor's COA.
    return packageCoaOptions.find(matchesSelection)
      ?? (retainedSelectedPackage?.context === packageSelectionContext
        && matchesSelection(retainedSelectedPackage.option) ? retainedSelectedPackage.option : null);
  }, [packageCoaOptions, form.packageId, form.packageTag, packageSelectionContext, retainedSelectedPackage]);

  const selectedPackageLabel = selectedPackage?.coaData?.packageTag || selectedPackage?.label || form.packageTag || initialPackageLabel || null;
  // RID-3165: prompt for a Metrc sign-in up front instead of after Print is clicked.
  useMetrcSignInCheck({ organizationId, marketCode: selectedPackage?.marketCode, facilityId: selectedPackage?.facilityId });
  const multiPackageSource = useMemo(() => {
    const sourcePackageId = Number(selectedPackage?.id);
    const sourceMetrcPackageId = Number(selectedPackage?.metrcPackageId);
    const facilityId = Number(selectedPackage?.facilityId);
    if (!organizationId || !Number.isSafeInteger(sourcePackageId) || sourcePackageId <= 0
      || !Number.isSafeInteger(sourceMetrcPackageId) || sourceMetrcPackageId <= 0
      || !Number.isSafeInteger(facilityId) || facilityId <= 0 || !selectedPackageLabel) return null;
    return {
      organizationId,
      sourcePackageId,
      sourceMetrcPackageId,
      facilityId,
      packageLabel: selectedPackageLabel,
      quantity: Number(selectedPackage?.coaData?.quantity),
    };
  }, [organizationId, selectedPackage?.id, selectedPackage?.metrcPackageId, selectedPackage?.facilityId,
    selectedPackage?.coaData?.quantity, selectedPackageLabel]);
  // A template-only draft is valid. A selected staged package must have its
  // authoritative row identity before saving; rendering JSON is never an ID source.
  const isStagedContextReady = (!form.isStaged || !selectedPackageLabel || Boolean(
    stagedPackageCoaIdRef.current
    && !isLoadingInitialPrintContext
    && !isLoadingCoa
    && !isStagedCoaDataInvalid,
  )) && (!reuseIssuedStagedCoa || issuedStagedPrintCoa != null);
  const {
    data: selectedProductData,
    error: selectedProductLookupError,
    isLoading: isLoadingProduct,
    refetch: retrySelectedProductLookup,
  } = useProduct(organizationId, selectedPackage?.productId);
  const selectedProductLookupFailed = Boolean(selectedProductLookupError);

  // Use the fetched COA data if available, otherwise fall back to package's basic coaData
  const usesTac = templateUsesTac(selectedTemplate);
  const { data: tacMarkets, error: tacMarketError, isLoading: isLoadingTacMarkets, refetch: retryTacMarkets } = useMarkets({
    organizationId, enabled: usesTac,
  });
  const { data: coaData, savedTacContextError } = useMemo(() => {
    const packageData = selectedPackage?.coaData || {};
    const fetchedData = effectivePackageCoa || {};
    const mergedCoa = mergePackageDateSources(fetchedData,
      reuseIssuedCoa || reuseIssuedStagedCoa || reuseDraftCoa ? {} : packageData);
    // Normalize saved sources without filling absent historical dates from the live package.
    const packageCoa = withTacPackageIdentity(
      usesTac ? withTacMarketPolicy(mergedCoa, tacMarkets, selectedPackage?.marketCode) : mergedCoa,
      organizationId, selectedPackage?.facilityId, form.isStaged ? null : form.packageId,
      form.isStaged ? effectivePackageCoa?.coaId ?? selectedPackageCoa?.coaId : null,
    );
    if (!usesTac || !(reuseIssuedCoa || reuseIssuedStagedCoa) || !savedTemplateSnapshot || !initialPrintRun) {
      return { data: packageCoa, savedTacContextError: null };
    }
    try {
      return {
        data: restoreSavedTacContext(packageCoa, savedTemplateSnapshot, initialPrintRun, Number(organizationId)),
        savedTacContextError: null,
      };
    } catch (error) {
      return {
        data: packageCoa,
        savedTacContextError: getErrorMessage(error, 'The saved TAC context could not be restored.'),
      };
    }
  }, [effectivePackageCoa, selectedPackage, reuseIssuedCoa, reuseIssuedStagedCoa, reuseDraftCoa, organizationId,
    form.isStaged, form.packageId, selectedPackageCoa?.coaId, tacMarkets, usesTac, savedTemplateSnapshot, initialPrintRun]);
  const tacResolution = useMemo(() => resolveTemplateTac(coaData, selectedTemplate), [coaData, selectedTemplate]);

  const isHistoricalOverrideProduct = Boolean(
    resolvedOverrideProduct
    && form.productOverrideId != null
    && Number(resolvedOverrideProduct.id) === Number(form.productOverrideId)
    && !selectedListOverrideProduct,
  );

  const resolvedProductForPreview = useMemo(() => {
    if (useCustomProduct) return form.productNameCustom || coaData.productName || '';
    if (resolvedOverrideProduct) return resolvedOverrideProduct.name || form.productName || coaData.productName || '';
    return form.productName || coaData.productName || '';
  }, [form.productName, form.productNameCustom, coaData.productName, useCustomProduct, resolvedOverrideProduct]);

  const productDataForPreview = useMemo(() => {
    if (resolvedOverrideProduct) {
      return resolvedOverrideProduct;
    }

    if (useCustomProduct && selectedProductData) {
      return {
        ...selectedProductData,
        name: resolvedProductForPreview,
      };
    }

    return form.productOverrideId == null ? selectedProductData : null;
  }, [form.productOverrideId, resolvedOverrideProduct, useCustomProduct, selectedProductData, resolvedProductForPreview]);

  const coaDataForPreview = useMemo(() => {
    const datedCoa = withCalculatedPrintDates(
      coaData,
      form.packagedDate || coaData.packagedDate || '',
      getEffectiveShelfLifeDays(productDataForPreview?.shelfLifeDays, effectiveDateTemplate?.shelfLifeDays),
      usedDateVariables,
    );
    if (!resolvedProductForPreview) return datedCoa;

    const resolved = resolveProductFields(resolvedOverrideProduct, null, coaData);

    return {
      ...datedCoa,
      productName: resolvedProductForPreview,
      ProductName: resolvedProductForPreview,
      ...(resolved.strain ? {
        strainName: resolved.strain,
        StrainName: resolved.strain,
        strain: resolved.strain,
        Strain: resolved.strain,
      } : {}),
      ...(resolved.productCategoryName ? {
        productCategoryName: resolved.productCategoryName,
        ProductCategoryName: resolved.productCategoryName,
      } : {}),
      ...(resolved.subSpecies ? {
        subSpecies: resolved.subSpecies,
        SubSpecies: resolved.subSpecies,
        subspeciesName: resolved.subSpecies,
      } : {}),
    };
  }, [
    coaData,
    resolvedProductForPreview,
    resolvedOverrideProduct,
    form.packagedDate,
    productDataForPreview?.shelfLifeDays,
    effectiveDateTemplate?.shelfLifeDays,
    usedDateVariables,
  ]);

  const tacPrintOptions = useMemo(() => {
    if (!selectedTemplate || !usesTac) return null;
    const renderTemplate = initialValues?.printRunId && !isDraft ? selectedTemplate : {
      ...selectedTemplate,
      themeVariables: selectedTemplate.themeVariables?.map(variable => ({
        ...variable,
        value: themeOverrides[variable.key] ?? variable.value,
        ...(variable.dateKind && dateStates[variable.key] ? { dateState: dateStates[variable.key] } : {}),
      })),
    };
    return {
      template: renderTemplate, coa: coaDataForPreview, job: form, product: productDataForPreview ?? {},
      hasCoa: hasCoaForOutput,
      labelCount: Number(form.count) || 1,
      common: {
        packageId: selectedPackage?.id, packageTag: coaData.packageTag || '',
        product: resolvedProductForPreview, market: coaData.market || '', facility: coaData.facility || '',
        packagedDate: form.packagedDate || coaData.packagedDate || '', printedDate: form.printedDate,
        count: Number(form.count) || 1, jobId: form.jobId, jobName: form.name,
      },
    };
  }, [selectedTemplate, usesTac, initialValues?.printRunId, isDraft, themeOverrides, dateStates,
    coaDataForPreview, form, productDataForPreview, hasCoaForOutput, selectedPackage?.id, coaData, resolvedProductForPreview]);
  const tacPrintOutput = useMemo<TacPrintOutput>(() => tacPrintOptions ? resolveTacPrintOutput({
    ...tacPrintOptions,
    qrValues: !isDraft ? existingQrValues ?? undefined : undefined,
    qrValuesPending: outputPlan.createsPdfPrintRun || isDraft || !existingQrValues?.length,
  }) : { kind: 'percent', values: [], outputs: [], summary: { calculated: 0, unavailable: 0 } }, [tacPrintOptions, existingQrValues, isDraft, outputPlan.createsPdfPrintRun]);
  const printedTacResolution = tacPrintOutput.outputs.find(output => output.provenance === 'edited')
    ?? tacPrintOutput.outputs.find(output => output.provenance === 'calculated')
    ?? tacPrintOutput.outputs[0];
  const printedTacValues = tacPrintOutput.values;
  const tacAcknowledgmentKey = JSON.stringify([tacContextKey, tacResolution, selectedTemplate, tacPrintOutput]);
  const requiresTacAcknowledgment = outputMode !== 'csv' && usesTac && printedTacValues.length > 0 &&
    (printedTacResolution?.provenance === 'calculated' || printedTacResolution?.provenance === 'edited');

  // A lightweight identity token marks the exact render inputs used by a preview.
  // Asset-map identity is included without serializing their potentially large base64 values.
  const printRenderContextToken = useMemo(() => ({}), [
    isDraftOutputBlocked,
    selectedApiTemplateKey,
    selectedTemplate,
    selectedLayout,
    selectedPackage?.id,
    form.name,
    form.jobId,
    form.count,
    form.packagedDate,
    form.printedDate,
    coaDataForPreview,
    productDataForPreview,
    resolvedProductForPreview,
    effectivePageRotation,
    topOffset,
    leftOffset,
    themeOverrides,
    legacyImages,
    legacyFonts,
    newAssetImages,
    newAssetFonts,
  ]);
  const currentPrintRenderContextRef = useRef(printRenderContextToken);
  currentPrintRenderContextRef.current = printRenderContextToken;

  const isDraftTemplateReady = Boolean(
    form.template
    && Number.isFinite(Number(selectedApiTemplate?.id))
    && Number(selectedApiTemplate?.id) > 0
    && selectedTemplate,
  );

  const isPrintContextLoaded = Boolean(
    organizationId
    && isStagedContextReady
    && !isDraftOutputBlocked
    && selectedApiTemplate?.id
    && selectedTemplate
    && selectedLayout
    && selectedPackage
    && !isLoadingInitialPrintContext
    && !isLoadingTemplates
    && !isLoadingPackages
    && !isLoadingCoa
    && !isLoadingProduct
    && !selectedProductLookupFailed
    && !isLoadingHistoricalOverride
    && !historicalOverrideLookupFailed,
  );
  const isPrintContextReady = Boolean(
    isPrintContextLoaded
    && !savedTacContextError
    && (!usesTac || !isLoadingTacMarkets)
    && !isLoadingAssets
    && !isLoadingNewAssets
    && !isGeneratingPdf
    && (
      !selectedFormatCaps.supportsPageLayout
      || (pdfPreviewUrl && renderedPreviewContextToken === printRenderContextToken)
    ),
  );

  // Debounce package search input
  useEffect(() => {
    if (initialValues?.printRunId || initialValues?.isStaged || form.isStaged) return;
    const timer = setTimeout(() => {
      setPackageSearchDebounced(packageSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [packageSearch, initialValues?.printRunId, initialValues?.isStaged, form.isStaged]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTemplateSearchDebounced(templateSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [templateSearch]);

  const isTemplateSearchPending = templateSearch.trim() !== templateSearchDebounced;


  // Saved drafts and issued active/promoted prints use their owned snapshots.
  // Only unsaved package selections resolve the current canonical COA here.
  useEffect(() => {
    if (reuseIssuedCoa || reuseDraftCoa || isDraftOutputBlocked) return;
    if (reuseIssuedStagedCoa) {
      stagedPackageCoaIdRef.current = issuedStagedPrintCoa?.coaId ?? null;
      setIsStagedCoaDataInvalid(issuedStagedPrintCoa == null);
      setIsLoadingCoa(false);
      return;
    }
    if ((!form.packageId && !form.packageTag) || !organizationId) {
      stagedPackageCoaIdRef.current = null;
      setIsStagedCoaDataInvalid(false);
      setSelectedPackageCoa(null);
      return;
    }

    let cancelled = false;
    setIsLoadingCoa(true);

    // Staged rows carry their database identity separately from the JSON COA payload.
    // The ID selects the exact facility-scoped row; data is only label-rendering content.
    if (form.isStaged) {
      const stagedCoaId = Number(initialValues?.stagedCoaId);
      const authoritativeId = Number.isSafeInteger(stagedCoaId) && stagedCoaId > 0
        ? stagedCoaId
        : null;
      stagedPackageCoaIdRef.current = authoritativeId;
      setIsStagedCoaDataInvalid(false);
      try {
        let parsedData: any = initialValues?.stagedCoaData ?? initialPackage?.stagedCoaData ?? initialPackage?.data ?? {};
        while (typeof parsedData === 'string') {
          parsedData = JSON.parse(parsedData);
        }
        if (parsedData !== null && (typeof parsedData !== 'object' || Array.isArray(parsedData))) {
          throw new Error('Staged COA rendering data must be an object or null.');
        }
        const fullCOA = normalizeCoaKeys({
          ...(parsedData || {}),
          coaId: authoritativeId,
        });
        setSelectedPackageCoa(fullCOA);
      } catch (e) {
        console.error('[StartPrintDialog] Failed to parse staged COA data:', e);
        setIsStagedCoaDataInvalid(true);
        setSelectedPackageCoa(null);
      } finally {
        setIsLoadingCoa(false);
      }
      return;
    }

    stagedPackageCoaIdRef.current = null;
    setIsStagedCoaDataInvalid(false);

    // attempt to fetch from cache
    const cached = packageCoaCacheRef.current[String(form.packageId)];
    if (cached) {
      setSelectedPackageCoa(cached);
      setIsLoadingCoa(false);
      return () => {
        cancelled = true;
      };
    }

    // get coa override data
    (async () => {
      try {
        if (!form.packageId) {
          setSelectedPackageCoa(null);
          return;
        }

        const rawResp = await getCOAByPackageId(organizationId, form.packageId);
        if (cancelled) return;
        // Normalize response (same logic as EditPackageDialog)
        const coadata: any = getEditableCoaPayload(rawResp) ?? rawResp;
        try {
          // Parse data field if it's a JSON string
          let parsedData: any = null;
          if (typeof coadata?.data === 'string') {
            try {
              parsedData = JSON.parse(coadata.data);
            } catch {
              parsedData = null;
            }
            while (typeof parsedData === 'string') {
              try {
                parsedData = JSON.parse(parsedData);
              } catch {
                break;
              }
            }
          } else if (typeof coadata?.data === 'object' && coadata.data !== null) {
            parsedData = coadata.data;
          }

          const fullCOA = normalizeCoaKeys({
            ...(coadata || {}),
            ...(parsedData || {}),
            coaId: coadata?.id ?? coadata?.Id,
          });
          setSelectedPackageCoa(fullCOA);
        } catch {
          setSelectedPackageCoa(coadata);
        }
      } catch (err) {
        console.error('[StartPrintDialog] Failed to fetch COA for package:', err);
        if (!cancelled) setSelectedPackageCoa(null);
      } finally {
        if (!cancelled) setIsLoadingCoa(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    form.packageId,
    form.packageTag,
    form.isStaged,
    reuseIssuedCoa,
    reuseIssuedStagedCoa,
    issuedStagedPrintCoa,
    reuseDraftCoa,
    isDraftOutputBlocked,
    organizationId,
    initialValues?.stagedCoaId,
    initialValues?.stagedCoaData,
    initialPackage?.data,
    initialPackage?.stagedCoaData,
  ]);

  useEffect(() => {
    let cancelled = false;
    const value = coaData.packageTag || selectedPackage?.id || '';
    if (!value) {
      setQrDataUrl('');
      return () => {
        cancelled = true;
      };
    }

    QRCode.toDataURL(value, { margin: 1, width: 80, version: 3 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });

    return () => {
      cancelled = true;
    };
  }, [coaData.packageTag, selectedPackage?.id]);

  useEffect(() => {
    if (!selectedTemplate || !legacyAssetResolver) {
      setLegacyImages({});
      setLegacyFonts({});
      return;
    }

    let cancelled = false;
    setIsLoadingAssets(true);

    legacyAssetResolver
      .preloadTemplateAssets(selectedTemplate)
      .then(({ images, fonts }) => {
        if (!cancelled) {
          setLegacyImages(images);
          setLegacyFonts(fonts);
        }
      })
      .catch((err) => {
        console.error('[StartPrintDialog] Failed to load legacy assets:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAssets(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedApiTemplateKey, selectedTemplate, legacyAssetResolver]);

  // Preload new asset images (uploaded via WYSIWYG) when template changes
  useEffect(() => {
    if (!selectedTemplate || !organizationId) {
      setNewAssetImages({});
      setNewAssetFonts({});
      return;
    }

    const hasNewAssetImages = selectedTemplate.elements.some((el) => {
      if (el.type === 'image') {
        const imgEl = el as ImageElement;
        if (imgEl.imageAsset?.blobName) return true;
        if (imgEl.conditions?.some((cond) => cond.imageAsset?.blobName)) return true;
      }
      return false;
    });
    const hasNewAssetFonts = selectedTemplate.elements.some(
      (el) => el.type === 'text' && (el as TextElement).fontAsset?.blobName,
    );
    if (!hasNewAssetImages && !hasNewAssetFonts) {
      setNewAssetImages({});
      setNewAssetFonts({});
      return;
    }

    let cancelled = false;
    setIsLoadingNewAssets(true);

    preloadNewAssets(selectedTemplate, assetApi, organizationId, getToken)
      .then(({ images, fonts }) => {
        if (!cancelled) {
          setNewAssetImages(images);
          setNewAssetFonts(fonts);
        }
      })
      .catch((err) => {
        console.error('[StartPrintDialog] Failed to load new assets:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingNewAssets(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedApiTemplateKey, selectedTemplate, assetApi, organizationId, getToken]);

  const pdfUrlRef = useRef(pdfPreviewUrl);
  pdfUrlRef.current = pdfPreviewUrl;
  const invalidateDraftOutput = useCallback(() => {
    // Set the ref synchronously as well: a pending generation can finish before
    // React commits the response's newly unavailable capture state.
    draftOutputBlockedRef.current = true;
    if (pdfUrlRef.current) revokePdfUrl(pdfUrlRef.current);
    pdfUrlRef.current = null;
    setPdfPreviewUrl(null);
    setRenderedPreviewContextToken(null);
  }, []);
  useEffect(() => {
    if (isDraftOutputBlocked) invalidateDraftOutput();
  }, [isDraftOutputBlocked, invalidateDraftOutput]);

  const isGeneratingRef = useRef(false);
  isGeneratingRef.current = isGeneratingPdf;

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        revokePdfUrl(pdfUrlRef.current);
      }
    };
  }, []);

  // Standard users only see active options. Superusers can reassign to archived options.
  const productOptions = useMemo(() => {
    const sourceById = new Map<number, Product>();
    products.forEach((product) => {
      if (Number.isFinite(product.id) && product.id > 0) {
        sourceById.set(product.id, product);
      }
    });

    const sorted = getProductOptions(products, allowArchivedProductOverrides).map((product) => {
      const source = sourceById.get(product.id);
      return {
        ...product,
        archived: source ? !isActiveProduct(source) : false,
      };
    });

    const counts = sorted.reduce((acc, p) => {
      const key = p.archived ? `${p.name} (archived)` : p.name;
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map<string, number>());
    const indexes = new Map<string, number>();

    return sorted.map((p) => {
      const baseName = p.archived ? `${p.name} (archived)` : p.name;
      const index = (indexes.get(baseName) || 0) + 1;
      indexes.set(baseName, index);
      const hasDuplicate = (counts.get(baseName) || 0) > 1;
      return { ...p, displayName: hasDuplicate ? `${baseName} (${index})` : baseName };
    });
  }, [products, allowArchivedProductOverrides]);
  const filteredProductOptions = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return productOptions;
    return productOptions.filter((p) => p.name.toLowerCase().includes(term));
  }, [productOptions, productSearch]);
  const historicalOverrideName = useMemo(() => {
    if (!resolvedOverrideProduct || !isHistoricalOverrideProduct || selectedListOverrideProduct) return null;
    if (productOptions.some((option) => option.id === form.productOverrideId)) return null;
    return resolvedOverrideProduct.name;
  }, [
    resolvedOverrideProduct,
    isHistoricalOverrideProduct,
    selectedListOverrideProduct,
    productOptions,
    form.productOverrideId,
  ]);
  const productOptionBaseStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    padding: '10px',
    borderRadius: 10,
    marginBottom: 6,
  } as const;

   // Server-side search - packages are already filtered, just paginate
  const totalPages = Math.max(1, Math.ceil(packageCoaOptions.length / pageSize));
  const pagedPackages = useMemo(() => {
    const start = (page - 1) * pageSize;
    return packageCoaOptions.slice(start, start + pageSize);
  }, [packageCoaOptions, page, pageSize]);
  const duplicatePackageKeys = useMemo(() => {
    const counts = new Map<string, number>();
    packageCoaOptions.forEach((pkg) => {
      const key = `${pkg.label}\u0000${pkg.coaData.productName || ''}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set(
      packageCoaOptions
        .filter((pkg) => counts.get(`${pkg.label}\u0000${pkg.coaData.productName || ''}`) > 1)
        .map((pkg) => pkg.id),
    );
  }, [packageCoaOptions]);

  const setSearchAndResetPage = (value: string) => {
    setPackageSearch(value);
    setPage(1);
  };

  const validateChildSharedSettings = () => {
    const reelSize = Number(form.labelsPerReel);
    const reelError = form.splitIntoReels && (!Number.isSafeInteger(reelSize) || reelSize <= 0)
      ? 'Labels per reel must be a positive whole number'
      : undefined;
    setErrors((current) => {
      const next = { ...current };
      if (reelError) next.labelsPerReel = reelError;
      else delete next.labelsPerReel;
      return next;
    });
    return !reelError;
  };

  const validate = () => {
    const nextErrors: Record<string, any> = {};
    if (!form.template) nextErrors.template = 'Template is required';
    if (!form.packageId && !form.packageTag) {
      nextErrors.packageId = 'Package is required';
    } else if (!selectedPackage) {
      nextErrors.packageId = 'Package is still loading or unavailable';
    }
    if (!form.count || Number(form.count) <= 0) nextErrors.count = 'Count must be greater than 0';
    if (form.count && !Number.isInteger(Number(form.count))) nextErrors.count = 'Count must be a whole number';
    if (!form.printedDate) nextErrors.printedDate = 'Print Date is required';
    if (form.splitIntoReels) {
      if (!form.labelsPerReel || Number(form.labelsPerReel) <= 0) {
        nextErrors.labelsPerReel = 'Labels per reel must be greater than 0';
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const update = (field: string, value: any) => {
    if (isDraftSaved) setIsDraftSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateOutputMode = (mode: DraftOutputMode) => {
    setOutputMode(mode);
    if (isDraftSaved) setIsDraftSaved(false);
  };

  const handleProductOverrideSelect = (value: string) => {
    if (savedTemplateSnapshot) setRecalculateRestoredDates(true);
    if (value === '__custom') {
      setUseCustomProduct(true);
      setIsProductOverridden(true);
      update('productOverrideId', null);
      return;
    }

    setUseCustomProduct(false);
    if (value === '') {
      update('productName', coaData.productName || '');
      update('productOverrideId', null);
      return;
    }

    const selectedProduct = productOptions.find((p) => String(p.id) === value);
    setIsProductOverridden(true);
    update('productName', selectedProduct?.name || '');
    update('productOverrideId', Number(value));
  };

  const selectedProductOptionValue = useMemo(() => {
    if (useCustomProduct) return '__custom';
    if (form.productOverrideId != null) return String(form.productOverrideId);
    return '';
  }, [useCustomProduct, form.productOverrideId]);

  const handlePackageSelect = (pkgId: string) => {
    if (isPackageSelectionLocked || packageMode === 'children') return;
    packagedDateEditedRef.current = false;
    const pkg = packageCoaOptions.find((p) => p.id === pkgId);
    if (!pkg) return;
    setRetainedSelectedPackage({ context: packageSelectionContext, option: pkg });
    const packagedState = packagedDateVariable ? dateStates[packagedDateVariable.key] : undefined;
    const preserveConfirmedPackagedDate = Boolean(
      savedTemplateSnapshot ||
      packagedState?.provenance === 'manual' ||
      packagedState?.provenance === 'cleared',
    );
    setIsLoadingCoa(true);
    update('packageId', pkgId);
    update('packageTag', pkg?.label || '');
    setSelectedPackageCoa(null);
    if (pkg?.coaData.productName) {
      update('productName', pkg.coaData.productName);
      setUseCustomProduct(false);
      setIsProductOverridden(false);
      update('productOverrideId', null);
    }
    update('productNameCustom', '');
    if (!preserveConfirmedPackagedDate) {
      const resolvedPackagedDate = resolveAutomaticPrintDate(
        'packagedDate',
        pkg?.coaData || {},
        '',
      );
      const nextPackagedDate = formatDateInputValue(resolvedPackagedDate.value);
      update('packagedDate', nextPackagedDate);
      if (packagedDateVariable) {
        setThemeOverrides((current) => ({
          ...current,
          [packagedDateVariable.key]: nextPackagedDate,
        }));
        setDateStates((current) => ({
          ...current,
          [packagedDateVariable.key]: {
            provenance: 'source',
            source: nextPackagedDate ? resolvedPackagedDate.state.source : undefined,
          },
        }));
      }
    }
    if (!countManuallyEditedRef.current && initialValues?.printRunId == null && form.printRunId == null) {
      update('count', toPackagePrintCount(pkg?.coaData?.quantity));
    }
  };

  const effectiveShelfLifeDays = getEffectiveShelfLifeDays(
    productDataForPreview?.shelfLifeDays,
    effectiveDateTemplate?.shelfLifeDays,
  );

  const getAutomaticDate = (kind: DateKind, confirmedPackagedDate = form.packagedDate) => resolveAutomaticPrintDate(
    kind,
    coaData,
    confirmedPackagedDate,
    effectiveShelfLifeDays,
  );

  const changePackagedDate = (value: string) => {
    packagedDateEditedRef.current = true;
    if (savedTemplateSnapshot && isDraft) setRecalculateRestoredDates(true);
    update('packagedDate', value);
  };

  useEffect(() => {
    if (!effectiveDateTemplate || usedDateVariables.length === 0) return;
    if (initialValues?.printRunId && !isDraft) return;

    const nextOverrides = { ...themeOverrides };
    const nextStates = { ...dateStates };
    let overridesChanged = false;
    let statesChanged = false;
    const packagedVariable = usedDateVariables.find((variable) => variable.dateKind === 'packagedDate');
    const hasPackagedOverride = packagedVariable &&
      Object.prototype.hasOwnProperty.call(themeOverrides, packagedVariable.key);
    const packagedResolved = packagedVariable && !hasPackagedOverride
      ? getAutomaticDate('packagedDate')
      : null;
    const confirmedPackagedDate = packagedVariable
      ? String(themeOverrides[packagedVariable.key] ?? packagedResolved?.value ?? form.packagedDate)
      : form.packagedDate;
    const previousInputs = automaticDateInputsRef.current;
    const sourceSignature = getDateSourceSignature(coaData);
    const automaticInputsChanged = previousInputs != null && (
      previousInputs.packagedDate !== confirmedPackagedDate ||
      previousInputs.shelfLifeDays !== effectiveShelfLifeDays ||
      previousInputs.sourceSignature !== sourceSignature
    );

    for (const variable of usedDateVariables) {
      const state = dateStates[variable.key];
      const resolved = variable.dateKind === 'packagedDate' && packagedResolved
        ? packagedResolved
        : getAutomaticDate(variable.dateKind!, confirmedPackagedDate);
      const isAutomatic = state?.provenance !== 'manual' && state?.provenance !== 'cleared';
      const shouldRefreshAutomatic = (!savedTemplateSnapshot || recalculateRestoredDates)
        && automaticInputsChanged && isAutomatic
        && nextOverrides[variable.key] !== resolved.value;
      const hasOverride = Object.prototype.hasOwnProperty.call(themeOverrides, variable.key);
      if (hasOverride && !shouldRefreshAutomatic) continue;
      if (nextOverrides[variable.key] !== resolved.value) {
        nextOverrides[variable.key] = resolved.value;
        overridesChanged = true;
      }
      if (
        nextStates[variable.key]?.provenance !== resolved.state.provenance ||
        nextStates[variable.key]?.source !== resolved.state.source
      ) {
        nextStates[variable.key] = resolved.state;
        statesChanged = true;
      }
    }

    automaticDateInputsRef.current = {
      packagedDate: confirmedPackagedDate,
      shelfLifeDays: effectiveShelfLifeDays,
      sourceSignature,
    };
    if (overridesChanged) setThemeOverrides(nextOverrides);
    if (statesChanged) setDateStates(nextStates);
    if (packagedVariable && form.packagedDate !== confirmedPackagedDate) {
      setForm((previous) => ({ ...previous, packagedDate: confirmedPackagedDate }));
    }
  }, [
    effectiveDateTemplate,
    usedDateVariables,
    coaData,
    effectiveShelfLifeDays,
    form.packagedDate,
    initialValues?.printRunId,
    isDraft,
    savedTemplateSnapshot,
    recalculateRestoredDates,
    themeOverrides,
    dateStates,
  ]);

  const updateConfirmedDate = (variable: ThemeVariable, value: string) => {
    const state: DateVariableState = { provenance: value ? 'manual' : 'cleared' };
    setThemeOverrides((current) => ({ ...current, [variable.key]: value }));
    setDateStates((current) => ({ ...current, [variable.key]: state }));
    if (variable.dateKind === 'packagedDate') {
      changePackagedDate(value);
    }
    if (isDraftSaved) setIsDraftSaved(false);
  };

  const resetConfirmedDate = (variable: ThemeVariable) => {
    if (!window.confirm(`Reset ${variable.name} to its automatic value?`)) return;
    const resolved = getAutomaticDate(
      variable.dateKind!,
      variable.dateKind === 'packagedDate' ? '' : form.packagedDate,
    );
    setThemeOverrides((current) => ({ ...current, [variable.key]: resolved.value }));
    setDateStates((current) => ({ ...current, [variable.key]: resolved.state }));
    if (variable.dateKind === 'packagedDate') {
      changePackagedDate(resolved.value);
      packagedDateEditedRef.current = false;
    }
    if (isDraftSaved) setIsDraftSaved(false);
  };

  const getTemplateWithOverrides = (printOutput = tacPrintOutput): Template | null => {
    if (!selectedTemplate) return null;
    const tacSnapshot = usesTac ? {
      tacPrintAudit: {
        ...(printOutput.outputs.find(output => output.provenance === 'edited')
          ?? printOutput.outputs.find(output => output.provenance === 'calculated')
          ?? printOutput.outputs[0] ?? { ...tacResolution, value: null, provenance: 'unavailable' as const }),
        outputs: printOutput.outputs,
        summary: printOutput.summary,
        packageKey: tacPackageKey(coaData),
        acknowledged: requiresTacAcknowledgment && tacAcknowledgment === tacAcknowledgmentKey,
        printedValues: printOutput.values,
        printedValueKind: printOutput.kind,
        ...(printOutput.provisional ? { printedValuesProvisional: true } : {}),
        packageId: form.isStaged || isStagedTacPackageKey(tacPackageKey(coaData))
          || form.packageId == null ? null : String(form.packageId),
        packageLabel: selectedPackageLabel,
      },
    } : {};
    if (initialValues?.printRunId && !isDraft) return withTacSources({ ...selectedTemplate, ...tacSnapshot }, coaData);
    const templateWithOverrides: Template = {
      ...selectedTemplate,
      ...tacSnapshot,
      selectedLayoutId,
      topOffset,
      leftOffset,
      pageRotation: effectivePageRotation,
      themeVariables: selectedTemplate.themeVariables?.map((v) => ({
        ...v,
        value: themeOverrides[v.key] ?? v.value,
        ...(v.dateKind && dateStates[v.key] ? { dateState: dateStates[v.key] } : {}),
      })),
      ...(usedDateVariables.length > 0 ? { confirmedDates: usedDateVariables.map((variable) => {
        const value = variable.dateKind === 'packagedDate'
          ? form.packagedDate
          : String(themeOverrides[variable.key] ?? variable.value ?? '');
        return {
          kind: variable.dateKind!,
          confirmedDate: value || null,
          isExplicitlyBlank: dateStates[variable.key]?.provenance === 'cleared',
          usedByTemplate: true,
        };
      }) } : {}),
    };
    if (usedDateVariables.length === 0) delete templateWithOverrides.confirmedDates;
    return withTacSources(templateWithOverrides, coaData);
  };

  const buildAddPrintRunPayload = (args: {
    templateId: number | string;
    templateData: Template | null;
  }) => {
    const packagedDate = form.packagedDate ? new Date(form.packagedDate) : null;
    const draftPrintSettings: DraftPrintSettings = {
      outputMode,
      splitIntoReels: form.splitIntoReels === true,
      labelsPerReel: Number(form.labelsPerReel) || 100,
    };
    const templateData = args.templateData
      ? {
          ...args.templateData,
          draftPrintSettings,
        }
      : null;
    return {
      organizationId,
      printRunId: isDraft && form.printRunId ? form.printRunId : undefined,
      jobId: form.jobId || null,
      jobName: form.name || null,
      templateId: Number(args.templateId),
      labelTemplateData: templateData ? JSON.stringify(templateData) : null,
      packageId: form.isStaged ? null : (form.packageId ? Number(form.packageId) : null),
      packageLabel: selectedPackage?.label || null,
      packageCoaId: form.isStaged ? null : (coaData.coaId ? Number(coaData.coaId) : null),
      stagePackageCoaId: form.isStaged
        ? stagedPackageCoaIdRef.current
        : null,
      count: Number(form.count) || 0,
      packagedDate: packagedDate && !Number.isNaN(packagedDate.getTime())
        ? packagedDate.toISOString()
        : null,
      productOverride:
        isProductOverridden || useCustomProduct ? form.productOverrideId || null : null,
      externalApp: 'Retail ID',
    };
  };

  const buildEditPrintRunPayload = () => {
    const packagedDate = form.packagedDate ? new Date(form.packagedDate) : null;
    const numericPackageId = Number(form.packageId);
    return {
      organizationId,
      printRunId: form.printRunId,
      jobId: form.jobId || null,
      jobName: form.name || null,
      templateId: selectedApiTemplate?.id
        ? Number(selectedApiTemplate.id)
        : (initialValues?.templateId ? Number(initialValues.templateId) : undefined),
      ...(usesTac && outputMode !== 'csv' ? { labelTemplateData: JSON.stringify(getTemplateWithOverrides()) } : {}),
      packageId: form.isStaged || !form.packageId || !Number.isFinite(numericPackageId)
        ? undefined
        : numericPackageId,
      packageLabel: selectedPackageLabel,
      stagePackageCoaId: form.isStaged ? stagedPackageCoaIdRef.current : null,
      count: Number(form.count) || 0,
      packagedDate: packagedDate && !Number.isNaN(packagedDate.getTime())
        ? packagedDate.toISOString()
        : null,
      productOverride:
        isProductOverridden || useCustomProduct ? form.productOverrideId || null : null,
    };
  };

  const createPrintRunRecord = useCallback(async () => {
    if (isDraftOutputBlocked) return null;
    const templateId = selectedApiTemplate?.id;
    if (!templateId) {
      console.error('Template ID not provided in payload');
      return null;
    }

    const templateWithThemeOverrides = getTemplateWithOverrides();

    try {
      const response = await addPrintRun(buildAddPrintRunPayload({
        templateId,
        templateData: templateWithThemeOverrides,
      }), pendingPrint.requestOptions());
      if (response) {
        if (!Number.isSafeInteger(Number(response.id)) || Number(response.id) <= 0 || !organizationId)
          throw new Error('Print run was not created');
        await validatePrintRunOutput(Number(organizationId), Number(response.id));
      }
      return response;
    } catch (error) {
      if (!isMountedRef.current) return null;
      // SSO organizations only: no Metrc sign-in works. The durable print request is kept exactly as before.
      if (handleSsoError(error)) {
        console.error(error);
        return null;
      }
      handlePrintRunError(error, 'Failed to create print run', Boolean(form.isStaged));
      return null;
    }
  }, [
    selectedApiTemplate?.id,
    isDraftOutputBlocked,
    pendingPrint.requestOptions,
    form,
    coaData.coaId,
    selectedPackage?.label,
    selectedTemplate,
    selectedLayoutId,
    topOffset,
    leftOffset,
    effectivePageRotation,
    themeOverrides,
    tacResolution,
    usesTac,
    tacAcknowledgment,
    tacAcknowledgmentKey,
    requiresTacAcknowledgment,
    organizationId,
    isProductOverridden,
    useCustomProduct,
    isDraft,
    handleSsoError,
  ]);

  const handleGeneratePdf = useCallback(
    async (
      isPreview: boolean = true,
      overrides?: {
        labelCount?: number;
        qrIndexOffset?: number;
        qrValues?: string[];
        skipPrintRun?: boolean;
        templateSnapshot?: Template;
        onQrValuesResolved?: (values: string[], printRunId?: number | string) => Promise<Template | null>;
      },
    ): Promise<{
      url: string | null;
      blob?: Blob;
      qrValues?: string[];
      printRunId?: number | string;
    }> => {
      if (isDraftOutputBlocked || !selectedTemplate || !selectedLayout || !selectedPackage) return { url: null };

      const count = overrides?.labelCount ?? (Number(form.count) || 1);
      const qrIndexOffset = overrides?.qrIndexOffset ?? 0;

      if (count <= 0) {
        setPdfError('Count must be greater than 0');
        return { url: null };
      }

      const previewLayout = computeLayout({
        layout: selectedLayout,
        labelCount: count,
        templateWidth: selectedTemplate.width,
        templateHeight: selectedTemplate.height,
        topOffset,
        leftOffset,
        rotation: effectivePageRotation,
      });
      const effectiveCount = isPreview ? Math.min(count, previewLayout.labelsPerPage) : count;

      setIsGeneratingPdf(true);
      setPdfError(null);
      if (isPreview) {
        setRenderedPreviewContextToken(null);
      }

      if (pdfUrlRef.current) {
        revokePdfUrl(pdfUrlRef.current);
        setPdfPreviewUrl(null);
      }

      let createdPrintRunId: number | string | null = null;
      try {
        const packageTag = coaData.packageTag || selectedPackage?.id;

        let templateWithThemeOverrides = overrides?.templateSnapshot ?? getTemplateWithOverrides()!;

        let response = null;
        if (!isPreview && !overrides?.skipPrintRun) {
          response = await createPrintRunRecord();
          if (!response) {
            return { url: null };
          }
          if (response?.id != null) {
            createdPrintRunId = response.id;
          }
        }

        // Generate QR values using RetailID SDK if package tag is valid (24-char hex)
        // For new print runs (preview), use indices 0 to effectiveCount-1
        // For saved print runs with ranges, those ranges would be passed from the print run data
        let qrValues: string[];
        if (overrides?.qrValues) {
          // Use pre-generated QR values for reel splitting
          qrValues = overrides.qrValues;
        } else if (response) {
          qrValues = response.qrs;
          if (!isPreview && Array.isArray(qrValues) && qrValues.length > 0) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || Number(form.count) || qrValues.length;
              return { generated: Math.min(total, qrValues.length), total };
            });
          }
        } else if (existingQrValues && existingQrValues.length > 0) {
          // Reprint: use QR values fetched from the existing print run via getPrintRunById
          qrValues = existingQrValues;
          if (!isPreview) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || Number(form.count) || qrValues.length;
              return { generated: Math.min(total, qrValues.length), total };
            });
          }
        } else if (isValidPackageTag(packageTag)) {
          if (!isPreview) {
            qrValues = await generateRetailIdQrUrlsSequentialProgressive(
              packageTag,
              qrIndexOffset,
              effectiveCount,
              undefined,
              (generated, total) => {
                setQrGenerationProgress((prev) => {
                  const globalGenerated = qrIndexOffset + generated;
                  const globalTotal = prev?.total || Number(form.count) || qrIndexOffset + total;
                  return {
                    generated: Math.min(globalTotal, globalGenerated),
                    total: globalTotal,
                  };
                });
              },
            );
          } else {
            qrValues = generateRetailIdQrUrlsSequential(packageTag, qrIndexOffset, effectiveCount);
          }
        } else {
          qrValues = Array.from({ length: effectiveCount }, () => packageTag);
        }

        if (overrides?.onQrValuesResolved) {
          templateWithThemeOverrides = await overrides.onQrValuesResolved(qrValues, response?.id) ?? templateWithThemeOverrides;
        }
        const data = {
          common: {
            packageId: selectedPackage?.id,
            packageTag: coaData.packageTag || '',
            product: resolvedProductForPreview,
            market: coaData.market || '',
            facility: coaData.facility || '',
            packagedDate: form.packagedDate || coaData.packagedDate || '',
            printedDate: form.printedDate,
            count: count,
            jobId: form.jobId,
            jobName: form.name,
          },
          job: form,
          coa: coaDataForPreview,
          product: productDataForPreview,
          qrValues,
        };

        const result = await generateLabelPdf({
          template: templateWithThemeOverrides,
          layout: selectedLayout,
          labelCount: effectiveCount,
          rotation: effectivePageRotation,
          topOffset,
          leftOffset,
          qrIndexOffset,
          data,
          legacyImages,
          legacyFonts,
          newAssetImages,
          newAssetFonts,
          isPreview,
          hasCoa: hasCoaForOutput,
        });

        if (isPreview) {
          if (!isMountedRef.current || draftOutputBlockedRef.current
            || currentPrintRenderContextRef.current !== printRenderContextToken) {
            if (result.url) revokePdfUrl(result.url);
            return { url: null };
          }
          setPdfPreviewUrl(result.url);
          setRenderedPreviewContextToken(printRenderContextToken);
        }
        return { url: result.url, blob: result.blob, qrValues, printRunId: response?.id };
      } catch (err) {
        console.error('[StartPrintDialog] PDF generation failed:', err);
        setPdfError(err instanceof Error ? err.message : 'PDF generation failed');
        if (createdPrintRunId != null && organizationId) {
          try {
            await recordPrintRunError(createdPrintRunId, {
              organizationId,
              message: err instanceof Error ? err.message : String(err),
              detail: {
                trace: err instanceof Error ? err.stack : null,
                isPreview,
                name: err instanceof Error ? err.name : null,
              },
            });
          } catch (reportError) {
            console.warn('[StartPrintDialog] Failed to record print run error:', reportError);
          }
        }
        if (!isPreview) {
          throw err instanceof Error ? err : new Error('PDF generation failed');
        }
        return { url: null };
      } finally {
        setIsGeneratingPdf(false);
      }
    },
    [
      selectedTemplate,
      selectedLayout,
      selectedPackage,
      form.count,
      form.packagedDate,
      form.printedDate,
      form.jobId,
      form.name,
      coaData,
      coaDataForPreview,
      resolvedProductForPreview,
      productDataForPreview,
      effectivePageRotation,
      topOffset,
      leftOffset,
      legacyImages,
      legacyFonts,
      newAssetImages,
      newAssetFonts,
      themeOverrides,
      printRenderContextToken,
      createPrintRunRecord,
      isDraftOutputBlocked,
      organizationId,
    ],
  );

  useEffect(() => {
    if (isDraftOutputBlocked || !selectedTemplate || !selectedLayout || !selectedPackage) return;
    if (!selectedFormatCaps.supportsPageLayout) return;
    if (!form.count || Number(form.count) <= 0) return;

    if (
      isLoadingInitialPrintContext
      || isLoadingAssets
      || isLoadingNewAssets
      || isLoadingPackages
      || isLoadingTemplates
      || isLoadingCoa
      || isLoadingProduct
    ) return;
    if (isSaving) return;

    if (isGeneratingPdf || isGeneratingRef.current) return;
    if (pdfPreviewUrl && renderedPreviewContextToken === printRenderContextToken) return;

    const timeoutId = setTimeout(() => {
      if (!isGeneratingRef.current) {
        handleGeneratePdf(true);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    selectedApiTemplateKey,
    selectedLayout?.id,
    selectedPackage?.id,
    form.count,
    effectivePageRotation,
    topOffset,
    leftOffset,
    isLoadingAssets,
    isLoadingNewAssets,
    isLoadingInitialPrintContext,
    isLoadingPackages,
    isLoadingTemplates,
    isLoadingCoa,
    isLoadingProduct,
    isSaving,
    isGeneratingPdf,
    pdfPreviewUrl,
    renderedPreviewContextToken,
    printRenderContextToken,
    handleGeneratePdf,
    isDraftOutputBlocked,
    selectedFormatCaps.supportsPageLayout,
    themeOverrides,
  ]);

  useEffect(() => {
    if (selectedFormatCaps.supportsPageLayout) return;
    setIsLayoutDropdownOpen(false);
    if (pdfUrlRef.current) {
      revokePdfUrl(pdfUrlRef.current);
      setPdfPreviewUrl(null);
    }
    setRenderedPreviewContextToken(null);
    setPdfError(null);
  }, [selectedFormatCaps.supportsPageLayout]);

  const canManagePrintRuns =
    currentUser?.isSuperUser ||
    currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
    currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0;

  const canAdminPrintRuns = Boolean(currentUser?.isSuperUser
    || (Array.isArray(currentUser?.roleActions) && currentUser.roleActions.includes('AdminPrintRuns')));
  const restoredStatus = String(initialPrintRun?.status ?? initialValues?.status ?? '').toLowerCase();
  const canReassignIssuedPrintRun = canAdminPrintRuns && !isDraft
    && ['0', 'queued', '2', 'completed', '3', 'failed'].includes(restoredStatus);
  const canReassignProduct = Boolean(form.printRunId) && !isPrintStarted
    && ((isDraft && canManagePrintRuns) || canReassignIssuedPrintRun);
  const hasValidReassignmentProduct = Number.isSafeInteger(Number(form.productOverrideId))
    && Number(form.productOverrideId) > 0;
  const isIssuedReassignmentPending = Boolean(form.printRunId) && !isDraft
    && (isReassignMode || isReassignSaving || reassignConfirmPending);

  const handleSaveDraft = async () => {
    if (pendingPrint.blocked || hasUnresolvedDraftSave || isDraftSaving) return;
    if (isLoadingInitialPrintContext || (hasSavedDraftPackage && !selectedPackage)) return;
    if (draftCoaUnavailable) {
      showToastError('This draft has no readable captured COA. Delete the draft and start again.');
      return;
    }
    if (!canManagePrintRuns) {
      showToastError('User does not have permission to perform this action. Please contact your administrator.');
      return;
    }

    if (isLoadingCoa) {
      showToastError('Package COA data is still loading. Please wait before saving.');
      return;
    }

    if (!isStagedContextReady) {
      showToastError(isStagedCoaDataInvalid
        ? 'Staged COA data could not be read. Refresh the page before saving.'
        : 'Staged package data is still loading. Please wait or refresh the page.');
      return;
    }

    const templateId = Number(selectedApiTemplate?.id);
    const templateWithOverrides = getTemplateWithOverrides();
    const nextErrors: Record<string, any> = {};
    if (!form.template) {
      nextErrors.template = 'Template is required';
    } else if (!Number.isFinite(templateId) || templateId <= 0 || !templateWithOverrides) {
      nextErrors.template = 'Template is still loading';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsDraftSaving(true);
    try {
      const result = await saveDraftPrintRun(buildAddPrintRunPayload({
        templateId,
        templateData: templateWithOverrides,
      }));
      if (positiveId(result?.id) == null) {
        setUnresolvedDraftSaveContext(packageSelectionContext);
        invalidateDraftOutput();
        throw new Error(DRAFT_SAVE_IDENTITY_ERROR);
      }
      if (result?.id) {
        // A saved run must never fall back to current results, even if its response is incomplete.
        if (form.printRunId && Number(result.id) !== Number(form.printRunId)) {
          throw new Error('The saved response belongs to a different draft. Reopen this draft before continuing.');
        }
        setSavedDraftHasSelection(Boolean(form.packageId || (form.isStaged && selectedPackageLabel)));
        setSavedDraftRun(result);
        setForm((prev) => ({ ...prev, printRunId: result.id }));
        setIsDraft(true);
        if (!form.isStaged && form.packageId
          && !decodeDraftPrintCoa(result, result.id, form.packageId)) {
          invalidateDraftOutput();
          throw new Error('The saved draft has no readable captured COA. Delete the draft and start again.');
        }
      }
      if (form.isStaged && selectedPackageLabel) {
        const returnedStagePackageCoaId = Number(result?.stagePackageCoaId);
        if (!Number.isSafeInteger(returnedStagePackageCoaId) || returnedStagePackageCoaId <= 0) {
          throw new Error('The saved staged draft did not include its current COA ID.');
        }
        stagedPackageCoaIdRef.current = returnedStagePackageCoaId;
        // A draft save replaces the staged row without changing the edited package.
        setTacEdits(current => current?.key === tacContextKey ? {
          ...current,
          key: JSON.stringify([selectedApiTemplateKey, form.packageId, form.isStaged, returnedStagePackageCoaId]),
          packageKey: tacPackageKey(withTacPackageIdentity({}, organizationId, selectedPackage?.facilityId, null, returnedStagePackageCoaId))!,
        } : current);
        setSelectedPackageCoa((current) => ({
          ...(current || {}),
          coaId: returnedStagePackageCoaId,
        }));
      }
      if (result?.id) {
        setForm((prev) => ({ ...prev, printRunId: result.id }));
      }
      setSavedTemplateSnapshot(templateWithOverrides && rebindStagedTacAdjustment(templateWithOverrides, organizationId,
        form.isStaged ? result?.stagePackageCoaId : null));
      layoutSelectionDirtyRef.current = false;
      setIsDraft(true);
      setIsDraftSaved(true);
      showToastSuccess('Draft Saved');
    } catch (error) {
      showToastError(getErrorMessage(error, 'Failed to save draft'));
    } finally {
      setIsDraftSaving(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!canManagePrintRuns) {
      showToastError('User does not have permission to perform this action. Please contact your administrator.');
      return;
    }

    if (!form.printRunId || !onDeleteDraft) {
      showToastError('Unable to delete draft');
      return;
    }

    try {
      await onDeleteDraft(form.printRunId);
      if (!isMountedRef.current) return;
      setShowDeleteDraftConfirm(false);
      close();
    } catch (error) {
      if (!isMountedRef.current) return;
      showToastError(getErrorMessage(error, 'Failed to delete draft'));
      setShowDeleteDraftConfirm(false);
    }
  };

  const handleSave = async () => {
    if (pendingPrint.blocked || hasUnresolvedDraftSave || isDraftSaving
      || packageMode === 'children' || printSubmissionRef.current) return;
    if (!canManagePrintRuns) {
      showToastError('User does not have permission to perform this action. Please contact your administrator.');
      return;
    }
    if (isIssuedReassignmentPending) return;
    if (!isPrintContextReady) {
      showToastError('Print data is still loading. Please wait and try again.');
      return;
    }
    if (!validate()) return;
    if (requiresTacAcknowledgment && tacAcknowledgment !== tacAcknowledgmentKey) {
      setShowTacGate(true);
      return;
    }
    setShowTacGate(false);

    printSubmissionRef.current = true;
    setIsSaving(true);
    const releasePrintSubmission = () => {
      printSubmissionRef.current = false;
      setIsSaving(false);
    };

    if (form.printRunId && organizationId) {
      try {
        await validatePrintRunOutput(Number(organizationId), Number(form.printRunId));
      } catch (error) {
        if (!handleSsoError(error)) {
          showToastError(getErrorMessage(error, 'Unable to validate laboratory results for printing.'));
        }
        releasePrintSubmission();
        return;
      }
    }

    const { totalCount, labelsPerReel, reelCount, useUserReels, useMiniReels, createsPdfPrintRun } = outputPlan;
    // Capture draft state for this run — a draft becomes a real print run via
    // addPrintRun (which accepts the draft printRunId), NOT editPrintRun.
    const startingFromDraft = isDraft;

    // Re-enable inputs after a failed start. Draft runs also need their draft
    // context restored so the user can retry without losing it.
    const restoreStateOnFailure = () => {
      setIsPrintStarted(false);
      if (startingFromDraft) {
        setIsDraft(true);
        setIsDraftSaved(true);
      }
    };

    setIsSaving(true);
    setIsPrintStarted(true);
    setIsDraft(false);
    setIsDraftSaved(false);
    setSavingPhase('reels');
    setReelProgress(null);
    setReelPageStatus(null);
    setIsMiniReelMode(false);
    setQrGenerationProgress({ generated: 0, total: totalCount });
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const markReelPagesCompleted = (reelNum: number) => {
      setReelPageStatus((prev) => {
        if (!prev || prev.completedReels.includes(reelNum)) return prev;

        const completedReels = [...prev.completedReels, reelNum].sort((a, b) => a - b);
        const completedPages = completedReels.reduce((sum, currentReel) => {
          return sum + (prev.pageCounts[currentReel - 1] || 0);
        }, 0);

        return { ...prev, completedReels, completedPages };
      });
    };

    const { productNameCustom, ...rest } = form;
    const resolvedProduct = useCustomProduct ? productNameCustom : rest.productName;
    const productOverrideId =
      isProductOverridden || useCustomProduct ? rest.productOverrideId || null : null;

    if (form.printRunId && !startingFromDraft && !createsPdfPrintRun && organizationId) {
      try {
        await editPrintRun(buildEditPrintRunPayload());
        await validatePrintRunOutput(Number(organizationId), Number(form.printRunId));
      } catch (error) {
        console.error('[StartPrintDialog] Failed to update print run:', error);
        if (!handleSsoError(error)) {
          showToastError(getErrorMessage(error, 'Failed to update print run'));
        }
        setIsSaving(false);
        setSavingPhase('idle');
        releasePrintSubmission();
        return;
      }
    }

    let firstPdfUrl: string | null = null;
    let allQrValues: string[] = [];
    let printRunId: number | string | undefined;
    let outputTemplate = getTemplateWithOverrides();
    const finalizeTacAudit = async (qrValues = allQrValues, resolvedPrintRunId = printRunId) => {
      if (!tacPrintOptions) return outputTemplate;
      const finalOutput = resolveTacPrintOutput({ ...tacPrintOptions, qrValues });
      if (finalOutput.values.length > 0 &&
        finalOutput.outputs.some(output => output.provenance === 'calculated' || output.provenance === 'edited') &&
        tacAcknowledgment !== tacAcknowledgmentKey) {
        throw new Error('The final TAC output requires acknowledgment. Start print again to confirm it.');
      }
      outputTemplate = getTemplateWithOverrides(finalOutput);
      if (tacPrintOutput.provisional || JSON.stringify(finalOutput) !== JSON.stringify(tacPrintOutput)) {
        if (!resolvedPrintRunId) throw new Error('A print run is required to save the final TAC audit.');
        await editPrintRun({
          ...buildEditPrintRunPayload(),
          printRunId: resolvedPrintRunId,
          labelTemplateData: JSON.stringify(outputTemplate),
        });
      }
      return outputTemplate;
    };
    let renderedOutputBlob: Blob | null = null;
    const jobSlug = selectedFormatDef.fileExtension === 'pdf'
      ? printRunPackageFilenameBase(form.name, selectedPackageLabel, form.jobId)
      : printRunFilenameBase(form.name, form.jobId);
    const pdfs: Array<{
      key: string;
      label: string;
      filename: string;
      rangeStart: number;
      rangeEnd: number;
    }> = [];
    const storedPdfKeys = new Set<string>();
    const storeAttemptPdf = async (key: string, blob: Blob) => {
      await storePdf(key, blob);
      storedPdfKeys.add(key);
      ownedPdfKeysRef.current.add(key);
    };

    try {
    if (outputMode !== 'csv' && selectedTemplate && selectedLayout && selectedPackage) {
      if (!selectedFormatCaps.supportsReelSplitting) {
        try {
          const packageTag = coaData.packageTag || selectedPackage?.id;
          const templateId = selectedApiTemplate?.id;

          if (!templateId) {
            console.error('Template ID not provided in payload');
            setIsSaving(false);
            setSavingPhase('idle');
            restoreStateOnFailure();
            return;
          }

          if (!form.printRunId || startingFromDraft) {
            const response = await createPrintRunRecord();
            if (!response) {
              setIsSaving(false);
              setSavingPhase('idle');
              restoreStateOnFailure();
              return;
            }

            printRunId = response?.id;

            if (response?.qrs) {
              allQrValues = response.qrs;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag || '');
            }
          } else if (form.printRunId) {
            printRunId = form.printRunId;
            if (existingQrValues && existingQrValues.length > 0) {
              allQrValues = existingQrValues;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag || '');
            }
          }

          if (allQrValues.length > 0) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || totalCount;
              return { generated: Math.min(total, allQrValues.length), total };
            });
          }

          await finalizeTacAudit();
          const templateWithThemeOverrides = outputTemplate!;
          setSavingPhase('reels');
          const result = await selectedFormatDef.renderLabels({
            template: {
              ...templateWithThemeOverrides,
              pageRotation: effectivePageRotation,
            },
            layout: selectedLayout,
            labelCount: totalCount,
            rotation: effectivePageRotation,
            topOffset,
            leftOffset,
            qrIndexOffset: 0,
            data: {
              common: {
                packageId: selectedPackage?.id,
                packageTag: coaData.packageTag || '',
                product: resolvedProductForPreview,
                market: coaData.market || '',
                facility: coaData.facility || '',
                packagedDate: form.packagedDate || coaData.packagedDate || '',
                printedDate: form.printedDate,
                count: totalCount,
                jobId: form.jobId,
                jobName: form.name,
              },
              coa: coaDataForPreview,
              product: productDataForPreview,
              job: form,
              qrValues: allQrValues,
            },
            legacyImages,
            legacyFonts,
            newAssetImages,
            newAssetFonts,
            hasCoa: hasCoaForOutput,
          });
          if (!result?.blob) {
            if (result?.url) URL.revokeObjectURL(result.url);
            throw new Error(`${selectedFormatDef.displayName} generation produced no output`);
          }
          setGeneratedOutput((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return {
              blob: result.blob,
              url: result.url,
              labelCount: result.labelCount,
              extra: result.extra,
            };
          });
          renderedOutputBlob = result.blob;
        } catch (err) {
          console.error(`${selectedFormatDef.displayName} generation failed:`, err);
          throw err instanceof Error
            ? err
            : new Error(`${selectedFormatDef.displayName} generation failed`);
        }
      } else if (useUserReels) {
        const pdfCacheId = createPdfCacheId();
        const pageCounts: number[] = Array.from({ length: reelCount }, (_, reelIndex) => {
          const reelLabelCount = Math.min(labelsPerReel, totalCount - reelIndex * labelsPerReel);
          return computeLayout({
            layout: selectedLayout,
            labelCount: reelLabelCount,
            templateWidth: selectedTemplate.width,
            templateHeight: selectedTemplate.height,
            topOffset,
            leftOffset,
            rotation: effectivePageRotation,
          }).pageCount;
        });
        const totalPages = pageCounts.reduce((sum, value) => sum + value, 0);
        setReelPageStatus({
          totalReels: reelCount,
          totalPages,
          pageCounts,
          completedPages: 0,
          completedReels: [],
        });

        let reelCompletedCount = 0;
        const activeReels = new Set<number>();
        setReelProgress({ completed: 0, total: reelCount, inFlight: [] });

        {
          const packageTag = coaData.packageTag || selectedPackage?.id;

          if (!form.printRunId || startingFromDraft) {
            const response = await createPrintRunRecord();
            if (!response) {
              setIsSaving(false);
              setSavingPhase('idle');
              restoreStateOnFailure();
              return;
            }

            printRunId = response?.id;

            if (response?.qrs) {
              allQrValues = response.qrs;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag);
            }
          } else {
            // Reprint: use QR values fetched from the existing print run via getPrintRunById
            printRunId = form.printRunId;

            if (existingQrValues && existingQrValues.length > 0) {
              allQrValues = existingQrValues;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag);
            }
          }

          if (allQrValues.length > 0) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || totalCount;
              return { generated: Math.min(total, allQrValues.length), total };
            });
          }
        }

        await finalizeTacAudit();
        //build all reels using workers
        const allReels: ReelAssignment[] = [];
        for (let reelIndex = 0; reelIndex < reelCount; reelIndex++) {
          const startIndex = reelIndex * labelsPerReel;
          const reelLabelCount = Math.min(labelsPerReel, totalCount - startIndex);
          const reelNum = reelIndex + 1;
          allReels.push({
            reelNum,
            labelCount: reelLabelCount,
            qrIndexOffset: startIndex,
            rangeStart: startIndex + 1,
            rangeEnd: startIndex + reelLabelCount,
            idbKey: `pdf-${pdfCacheId}-reel-${reelNum}`,
            filename: `${jobSlug}-reel-${reelNum}.pdf`,
          });
        }

        let workerSuccess = false;
        if (allReels.length > 0 && isWorkerSupported()) {
          try {
            const templateWithThemeOverrides = outputTemplate!;
            const { fullData, themeData, rawDataContext } = prepareRenderData({
              template: templateWithThemeOverrides,
              coa: coaDataForPreview,
              job: form,
              product: productDataForPreview,
              common: {
                packageId: selectedPackage?.id,
                packageTag: coaData.packageTag || '',
                product: resolvedProductForPreview,
                market: coaData.market || '',
                facility: coaData.facility || '',
                packagedDate: form.packagedDate || coaData.packagedDate || '',
                printedDate: form.printedDate,
                count: totalCount,
                jobId: form.jobId,
                jobName: form.name,
              },
            });

            const conditionTypeByKey = buildConditionTypeByKey(
              templateWithThemeOverrides.themeVariables,
            );

            // Workers generate QR codes, barcodes, and SVG shapes internally
            setSavingPhase('reels');
            await new Promise((r) => setTimeout(r, 0));

            const resolverContext: SerializableResolverContext = {
              data: fullData,
              rawDataContext,
              conditionTypeByKey,
              themeVariables: templateWithThemeOverrides.themeVariables,
              legacyImages,
              legacyFonts,
              newAssetImages,
              newAssetFonts,
              defaultFont: DEFAULT_FONT,
              isPreview: false,
              hasCoa: hasCoaForOutput,
            };

            const computedLayout = computeLayout({
              layout: selectedLayout,
              labelCount: labelsPerReel,
              templateWidth: templateWithThemeOverrides.width,
              templateHeight: templateWithThemeOverrides.height,
              topOffset,
              leftOffset,
              rotation: effectivePageRotation,
            });

            const layoutPlan = {
              pageWidthPx: computedLayout.pageWidthPx,
              pageHeightPx: computedLayout.pageHeightPx,
              labelAreaWidthPx: computedLayout.labelAreaWidthPx,
              labelAreaHeightPx: computedLayout.labelAreaHeightPx,
              positions: computedLayout.positions,
              labelsPerPage: computedLayout.labelsPerPage,
              pageCount: computedLayout.pageCount,
              pageRotation: computedLayout.pageRotation,
              labelRotation: computedLayout.labelRotation,
            };

            const sharedData: SharedWorkerData = {
              template: templateWithThemeOverrides,
              layout: layoutPlan,
              qrValues: allQrValues,
              commonData: fullData,
              themeData,
              resolverContext,
              fontSources: pdfFontSources,
              baseUrl: window.location.origin,
              defaultFont: DEFAULT_FONT,
              isPreview: false,
            };

            const effectiveCount = getEffectiveWorkerCount(workerCount);
            const pool = new PdfWorkerPool(
              sharedData,
              {
                workerCount: effectiveCount,
                onReelStart: (reelNum) => {
                  activeReels.add(reelNum);
                  setReelProgress({
                    completed: reelCompletedCount,
                    total: reelCount,
                    inFlight: Array.from(activeReels).sort((a, b) => a - b),
                  });
                },
                onReelComplete: (reelNum) => {
                  activeReels.delete(reelNum);
                  reelCompletedCount++;
                  setReelProgress({
                    completed: reelCompletedCount,
                    total: reelCount,
                    inFlight: Array.from(activeReels).sort((a, b) => a - b),
                  });
                  markReelPagesCompleted(reelNum);
                  setQrGenerationProgress((prev) => {
                    const total = prev?.total || totalCount;
                    const processed = Math.min(total, reelCompletedCount * labelsPerReel);
                    return { generated: Math.max(processed, prev?.generated || 0), total };
                  });
                },
              },
              allReels.length,
            );

            try {
              for (const reel of allReels) {
                pool.enqueue(reel);
              }
              pool.seal();

              const results = await pool.results();

              //dtore worker results in IndexedDB
              for (let i = 0; i < results.length; i++) {
                const result = results[i];
                const reel = allReels[i];
                const blob = new Blob([result.pdfBytes as BlobPart], { type: 'application/pdf' });
                await storeAttemptPdf(reel.idbKey, blob);
                pdfs.push({
                  key: reel.idbKey,
                  label: `Reel ${reel.reelNum}`,
                  filename: reel.filename,
                  rangeStart: reel.rangeStart,
                  rangeEnd: reel.rangeEnd,
                });
              }

              workerSuccess = true;
            } catch (poolErr) {
              pool.terminate();
              throw poolErr;
            }
          } catch (workerErr) {
            console.warn(
              '[StartPrintDialog] Worker-based generation failed, falling back to sequential:',
              workerErr,
            );
          }
        }

        //fallback: sequential generation for all reels on main thread
        if (!workerSuccess && allReels.length > 0) {
          for (const reel of allReels) {
            setReelProgress({
              completed: reelCompletedCount,
              total: reelCount,
              inFlight: [reel.reelNum],
            });

            const reelResult = await handleGeneratePdf(false, {
              labelCount: reel.labelCount,
              qrIndexOffset: reel.qrIndexOffset,
              qrValues: allQrValues,
              skipPrintRun: true,
              templateSnapshot: outputTemplate ?? undefined,
            });

            if (reelResult.blob) {
              await storeAttemptPdf(reel.idbKey, reelResult.blob);
              pdfs.push({
                key: reel.idbKey,
                label: `Reel ${reel.reelNum}`,
                filename: reel.filename,
                rangeStart: reel.rangeStart,
                rangeEnd: reel.rangeEnd,
              });
            }
            if (reelResult.url) {
              revokePdfUrl(reelResult.url);
            }
            reelCompletedCount++;
            setReelProgress({ completed: reelCompletedCount, total: reelCount, inFlight: [] });
            markReelPagesCompleted(reel.reelNum);
            setQrGenerationProgress((prev) => {
              const total = prev?.total || totalCount;
              const processed = Math.min(total, reelCompletedCount * labelsPerReel);
              return { generated: Math.max(processed, prev?.generated || 0), total };
            });
          }
        }

        setReelProgress(null);
        setReelPageStatus((prev) =>
          prev
            ? {
                ...prev,
                completedPages: prev.totalPages,
                completedReels: Array.from({ length: prev.totalReels }, (_, index) => index + 1),
              }
            : prev,
        );
      } else {
        const pdfCacheId = createPdfCacheId();
        //optimized single-PDF path: transparent mini-reel splitting
        if (useMiniReels) {
          setIsMiniReelMode(true);

          //compute labelsPerPage so mini-reel splits align to page boundaries.
          const { labelsPerPage: miniReelLabelsPerPage } = computeLayout({
            layout: selectedLayout,
            labelCount: 1,
            templateWidth: selectedTemplate.width,
            templateHeight: selectedTemplate.height,
            topOffset,
            leftOffset,
            rotation: effectivePageRotation,
          });

          const miniReelConfig = computeMiniReels(totalCount, pdfCacheId, miniReelLabelsPerPage);
          const { reels: miniReels, labelsPerReel: miniLabelsPerReel } = miniReelConfig;

          //compute page counts for progress tracking
          const pageCounts: number[] = miniReels.map((reel) => {
            return computeLayout({
              layout: selectedLayout,
              labelCount: reel.labelCount,
              templateWidth: selectedTemplate.width,
              templateHeight: selectedTemplate.height,
              topOffset,
              leftOffset,
              rotation: effectivePageRotation,
            }).pageCount;
          });
          const totalPages = pageCounts.reduce((sum, v) => sum + v, 0);
          setReelPageStatus({
            totalReels: miniReelConfig.reelCount,
            totalPages,
            pageCounts,
            completedPages: 0,
            completedReels: [],
          });

          let reelCompletedCount = 0;
          const activeReels = new Set<number>();
          setReelProgress({ completed: 0, total: miniReelConfig.reelCount, inFlight: [] });

          //create print run
          const packageTag = coaData.packageTag || selectedPackage?.id;

          if (!form.printRunId || startingFromDraft) {
            const response = await createPrintRunRecord();
            if (!response) {
              setIsSaving(false);
              setSavingPhase('idle');
              restoreStateOnFailure();
              return;
            }

            printRunId = response?.id;

            if (response?.qrs) {
              allQrValues = response.qrs;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag);
            }
          } else {
            printRunId = form.printRunId;

            if (existingQrValues && existingQrValues.length > 0) {
              allQrValues = existingQrValues;
            } else if (isValidPackageTag(packageTag)) {
              allQrValues = await generateRetailIdQrUrlsSequentialProgressive(
                packageTag,
                0,
                totalCount,
                undefined,
                (generated, _total) => {
                  setQrGenerationProgress((prev) => {
                    const globalTotal = prev?.total || totalCount;
                    return {
                      generated: Math.min(globalTotal, generated),
                      total: globalTotal,
                    };
                  });
                },
              );
            } else {
              allQrValues = Array.from({ length: totalCount }, () => packageTag);
            }
          }

          if (allQrValues.length > 0) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || totalCount;
              return { generated: Math.min(total, allQrValues.length), total };
            });
          }

          await finalizeTacAudit();
          const templateWithThemeOverrides = outputTemplate!;
          //build worker shared data
          let workerSuccess = false;
          try {
            const { fullData, themeData, rawDataContext } = prepareRenderData({
              template: templateWithThemeOverrides,
              coa: coaDataForPreview,
              job: form,
              product: productDataForPreview,
              common: {
                packageId: selectedPackage?.id,
                packageTag: coaData.packageTag || '',
                product: resolvedProductForPreview,
                market: coaData.market || '',
                facility: coaData.facility || '',
                packagedDate: form.packagedDate || coaData.packagedDate || '',
                printDate: form.printedDate,
                count: totalCount,
                jobId: form.jobId,
                jobName: form.name,
              },
            });

            const conditionTypeByKey = buildConditionTypeByKey(
              templateWithThemeOverrides.themeVariables,
            );

            setSavingPhase('reels');
            await new Promise((r) => setTimeout(r, 0));

            const resolverContext: SerializableResolverContext = {
              data: fullData,
              rawDataContext,
              conditionTypeByKey,
              themeVariables: templateWithThemeOverrides.themeVariables,
              legacyImages,
              legacyFonts,
              newAssetImages,
              newAssetFonts,
              defaultFont: DEFAULT_FONT,
              isPreview: false,
              hasCoa: hasCoaForOutput,
            };
            const computedLayout = computeLayout({
              layout: selectedLayout,
              labelCount: miniLabelsPerReel,
              templateWidth: templateWithThemeOverrides.width,
              templateHeight: templateWithThemeOverrides.height,
              topOffset,
              leftOffset,
              rotation: effectivePageRotation,
            });

            const layoutPlan = {
              pageWidthPx: computedLayout.pageWidthPx,
              pageHeightPx: computedLayout.pageHeightPx,
              labelAreaWidthPx: computedLayout.labelAreaWidthPx,
              labelAreaHeightPx: computedLayout.labelAreaHeightPx,
              positions: computedLayout.positions,
              labelsPerPage: computedLayout.labelsPerPage,
              pageCount: computedLayout.pageCount,
              pageRotation: computedLayout.pageRotation,
              labelRotation: computedLayout.labelRotation,
            };

            const sharedData: SharedWorkerData = {
              template: templateWithThemeOverrides,
              layout: layoutPlan,
              qrValues: allQrValues,
              commonData: fullData,
              themeData,
              resolverContext,
              fontSources: pdfFontSources,
              baseUrl: window.location.origin,
              defaultFont: DEFAULT_FONT,
              isPreview: false,
            };

            const effectiveCount = getEffectiveWorkerCount(workerCount);
            const pool = new PdfWorkerPool(
              sharedData,
              {
                workerCount: effectiveCount,
                onReelStart: (reelNum) => {
                  activeReels.add(reelNum);
                  setReelProgress({
                    completed: reelCompletedCount,
                    total: miniReelConfig.reelCount,
                    inFlight: Array.from(activeReels).sort((a, b) => a - b),
                  });
                },
                onReelComplete: (reelNum) => {
                  activeReels.delete(reelNum);
                  reelCompletedCount++;
                  setReelProgress({
                    completed: reelCompletedCount,
                    total: miniReelConfig.reelCount,
                    inFlight: Array.from(activeReels).sort((a, b) => a - b),
                  });
                  markReelPagesCompleted(reelNum);
                  setQrGenerationProgress((prev) => {
                    const total = prev?.total || totalCount;
                    const processed = Math.min(total, reelCompletedCount * miniLabelsPerReel);
                    return { generated: Math.max(processed, prev?.generated || 0), total };
                  });
                },
              },
              miniReels.length,
            );

            try {
              for (const reel of miniReels) {
                pool.enqueue(reel);
              }
              pool.seal();

              const results = await pool.results();

              //store worker results in IndexedDB
              const miniReelIdbKeys: string[] = [];
              for (let i = 0; i < results.length; i++) {
                const result = results[i];
                const reel = miniReels[i];
                const blob = new Blob([result.pdfBytes as BlobPart], { type: 'application/pdf' });
                await storeAttemptPdf(reel.idbKey, blob);
                miniReelIdbKeys.push(reel.idbKey);
              }

              setSinglePdfMiniReelKeys(miniReelIdbKeys);

              //show as single PDF entry in the UI
              pdfs.push({
                key: `pdf-${pdfCacheId}-merged`,
                label: 'Labels',
                filename: `${jobSlug}.pdf`,
                rangeStart: 1,
                rangeEnd: totalCount,
              });

              workerSuccess = true;
            } catch (poolErr) {
              pool.terminate();
              throw poolErr;
            }
          } catch (workerErr) {
            console.warn(
              '[StartPrintDialog] Optimized single-PDF worker generation failed, falling back to sequential:',
              workerErr,
            );
          }

          //fallback: sequential main-thread generation of mini-reels
          if (!workerSuccess) {
            const miniReelIdbKeys: string[] = [];
            for (const reel of miniReels) {
              setReelProgress({
                completed: reelCompletedCount,
                total: miniReelConfig.reelCount,
                inFlight: [reel.reelNum],
              });

              const reelResult = await handleGeneratePdf(false, {
                labelCount: reel.labelCount,
                qrIndexOffset: reel.qrIndexOffset,
                qrValues: allQrValues,
                skipPrintRun: true,
                templateSnapshot: outputTemplate ?? undefined,
              });

              if (reelResult.blob) {
                await storeAttemptPdf(reel.idbKey, reelResult.blob);
                miniReelIdbKeys.push(reel.idbKey);
              }
              if (reelResult.url) {
                revokePdfUrl(reelResult.url);
              }
              reelCompletedCount++;
              setReelProgress({
                completed: reelCompletedCount,
                total: miniReelConfig.reelCount,
                inFlight: [],
              });
              markReelPagesCompleted(reel.reelNum);
              setQrGenerationProgress((prev) => {
                const total = prev?.total || totalCount;
                const processed = Math.min(total, reelCompletedCount * miniLabelsPerReel);
                return { generated: Math.max(processed, prev?.generated || 0), total };
              });
            }

            if (miniReelIdbKeys.length > 0) {
              setSinglePdfMiniReelKeys(miniReelIdbKeys);
              pdfs.push({
                key: `pdf-${pdfCacheId}-merged`,
                label: 'Labels',
                filename: `${jobSlug}.pdf`,
                rangeStart: 1,
                rangeEnd: totalCount,
              });
            }
          }

          setReelProgress(null);
          setReelPageStatus((prev) =>
            prev
              ? {
                  ...prev,
                  completedPages: prev.totalPages,
                  completedReels: Array.from({ length: prev.totalReels }, (_, index) => index + 1),
                }
              : prev,
          );
        } else {
          //small job or workers unavailable
          const result = await handleGeneratePdf(false, { onQrValuesResolved: finalizeTacAudit });
          const generatedPrintRunId = Number(result.printRunId);
          if (
            result.printRunId != null &&
            (!Number.isFinite(generatedPrintRunId) || generatedPrintRunId <= 0)
          ) {
            throw new Error('Print run was not created');
          }
          if (result.printRunId == null) {
            restoreStateOnFailure();
            return;
          }
          firstPdfUrl = result.url;
          printRunId = result.printRunId;

          if (result.blob) {
            const key = `pdf-${pdfCacheId}`;
            await storeAttemptPdf(key, result.blob);
            pdfs.push({
              key,
              label: 'Labels',
              filename: `${jobSlug}.pdf`,
              rangeStart: 1,
              rangeEnd: totalCount,
            });
          }
          if (result.url) {
            revokePdfUrl(result.url);
          }
          if (result.qrValues?.length) {
            setQrGenerationProgress((prev) => {
              const total = prev?.total || totalCount;
              return { generated: Math.min(total, result.qrValues.length), total };
            });
          }
        }
      }
    }

    if (
      outputMode !== 'csv' &&
      selectedTemplate &&
      selectedLayout &&
      selectedPackage &&
      !selectedFormatCaps.supportsReelSplitting &&
      !renderedOutputBlob
    ) {
      throw new Error(
        `${selectedFormatDef?.displayName ?? 'Label'} generation produced no output`,
      );
    }

    setSavingPhase('finalizing');

    if (outputMode === 'csv' && !printRunId && (!form.printRunId || startingFromDraft)) {
      const response = await createPrintRunRecord();
      if (!response) {
        setIsSaving(false);
        setSavingPhase('idle');
        restoreStateOnFailure();
        return;
      }
      printRunId = response?.id;
    }

    if (!printRunId && form.printRunId) {
      printRunId = form.printRunId;
    }

    setGeneratedPdfs(pdfs);

    const templateWithOverrides = outputTemplate;
    const payload = {
      ...rest,
      productName: resolvedProduct,
      product: resolvedProduct,
      productOverride: productOverrideId,
      count: Number(form.count),
      pdfUrl: firstPdfUrl,
      templateId: selectedApiTemplate?.id,
      labelTemplateData: templateWithOverrides ? JSON.stringify(templateWithOverrides) : undefined,
      packageLabel: selectedPackageLabel,
    };

    if (!Number.isFinite(Number(printRunId)) || Number(printRunId) <= 0) {
      throw new Error('Print run was not created');
    }
    if (!organizationId) {
      throw new Error('Organization is required to complete the print run');
    }

    setCompletedPrintRun({ organizationId, id: printRunId });
    setDeferredSavePayload(payload);
    } catch (error) {
      console.error('[StartPrintDialog] handleSave failed:', error);
      if (storedPdfKeys.size > 0) {
        try {
          await clearPdfs(Array.from(storedPdfKeys));
          for (const key of storedPdfKeys) ownedPdfKeysRef.current.delete(key);
        } catch (cleanupError) {
          console.error('[StartPrintDialog] Failed to clear PDFs from the unsuccessful print attempt:', cleanupError);
          showToastError('Failed to remove temporary PDF files. Cleanup will be retried when this print is closed.');
        }
      }
      const message = getPrintRunErrorMessage(
        error,
        'Failed to complete print run',
        Boolean(form.isStaged),
      );
      showToastError(message);
      restoreStateOnFailure();

      const reportableId = printRunId ?? (startingFromDraft ? form.printRunId : undefined);
      if (reportableId && organizationId) {
        try {
          await recordPrintRunError(reportableId, {
            organizationId,
            message,
            detail: {
              trace: error instanceof Error ? error.stack : null,
              outputMode,
              splitIntoReels: Boolean(form.splitIntoReels),
              reelCount,
              totalCount,
              name: error instanceof Error ? error.name : null,
            },
          });
        } catch (reportError) {
          console.warn('[StartPrintDialog] Failed to record print run error:', reportError);
        }
      }
    } finally {
      releasePrintSubmission();
      setTacAcknowledgment(null);
      setSavingPhase('idle');
      setReelProgress(null);
      setReelPageStatus(null);
      setQrGenerationProgress(null);
    }
  };

  const executeReassignment = async () => {
    if (!organizationId || !form.printRunId || !canReassignProduct || isReassignSaving) return;
    if (!isDraft && !hasValidReassignmentProduct) return;
    if (!isDraft) {
      const result = await reassignIssued({
        organizationId,
        printRunId: Number(form.printRunId),
        productOverride: Number(form.productOverrideId),
      });
      if (result.success) {
        setIsReassignMode(false);
        savedProductStateRef.current = null;
      }
      return;
    }
    const result = await reassign({
      payload: buildEditPrintRunPayload(),
      isStaged: form.isStaged,
    });
    if (!result.success) return;

    setTacEdits(current => current?.key === tacContextKey ? {
      ...current,
      key: JSON.stringify([selectedApiTemplateKey, form.packageId, form.isStaged, result.coaId]),
      packageKey: tacPackageKey(withTacPackageIdentity({}, organizationId, selectedPackage?.facilityId,
        form.isStaged ? null : form.packageId, form.isStaged ? result.coaId : null))!,
    } : current);
    if (form.isStaged) stagedPackageCoaIdRef.current = result.coaId;
    if (form.isStaged) setSavedTemplateSnapshot(current => current
      ? rebindStagedTacAdjustment(current, organizationId, result.coaId) : current);
    setSelectedPackageCoa((current) => {
      const updatedCoa = { ...(current || {}), coaId: result.coaId };
      if (!form.isStaged && form.packageId) {
        packageCoaCacheRef.current[String(form.packageId)] = updatedCoa;
      }
      return updatedCoa;
    });
    setIsReassignMode(false);
    savedProductStateRef.current = null;

  };

  const handleClose = async () => {
    if (isReassignSaving) return;
    if (generatedOutput?.url) URL.revokeObjectURL(generatedOutput.url);
    const keys = Array.from(ownedPdfKeysRef.current);
    if (keys.length > 0) {
      try {
        await clearPdfs(keys);
        for (const key of keys) ownedPdfKeysRef.current.delete(key);
      } catch (error) {
        console.error('[StartPrintDialog] Failed to clear PDFs when closing the print:', error);
        showToastError('Failed to remove temporary PDF files.');
      }
    }
    if (deferredSavePayload) {
      onSave(deferredSavePayload);
    } else {
      close();
    }
  };

  //clean up IndexedDB and blob URLs on unmount
  const generatedOutputRef = useRef(generatedOutput);
  generatedOutputRef.current = generatedOutput;
  useEffect(() => {
    return () => {
      if (generatedOutputRef.current?.url) {
        URL.revokeObjectURL(generatedOutputRef.current.url);
      }
      const keys = Array.from(ownedPdfKeysRef.current);
      if (keys.length > 0) {
        clearPdfs(keys).catch((error) => {
          console.error('[StartPrintDialog] Failed to clear PDFs on unmount:', error);
        });
      }
    };
  }, []);

  const csvFilename = printRunCsvFilename(form.name, form.jobId, selectedPackageLabel);
  const csvBaseName = csvFilename.replace(/\.csv$/, '');
  const outputFilenameBase = selectedFormatDef.fileExtension === 'pdf'
    ? printRunPackageFilenameBase(form.name, selectedPackageLabel, form.jobId)
    : printRunFilenameBase(form.name, form.jobId);

  const handleExportCsv = async () => {
    if (!completedPrintRun) return;
    setIsExportingCsv(true);
    try {
      await exportPrintRunJob(completedPrintRun, undefined, csvFilename);
    } finally {
      setIsExportingCsv(false);
    }
  };

  const handleExportReelCsv = async (reelNum: number, rangeStart: number, rangeEnd: number) => {
    if (!completedPrintRun) return;
    setIsExportingCsv(true);
    try {
      await exportPrintRunReel(completedPrintRun, reelNum, rangeStart, rangeEnd, csvBaseName);
    } finally {
      setIsExportingCsv(false);
    }
  };

  const handleDownloadPdf = async (key: string, filename: string) => {
    try {
      //check if this is a merged single-PDF that needs mini-reel assembly
      if (singlePdfMiniReelKeys && singlePdfMiniReelKeys.length > 0 && key.endsWith('-merged')) {
        setMergeProgress({ phase: 'merging', merged: 0, total: singlePdfMiniReelKeys.length });
        try {
          await mergeAndStreamPdfs({
            reelKeys: singlePdfMiniReelKeys,
            filename,
            onMergeProgress: (merged, total) => {
              setMergeProgress({ phase: 'merging', merged, total });
            },
            onSerializing: () => {
              setMergeProgress((prev) => (prev ? { ...prev, phase: 'serializing' } : null));
            },
            onWriting: () => {
              setMergeProgress((prev) => (prev ? { ...prev, phase: 'writing' } : null));
            },
          });
        } catch (err) {
          //user cancelled the save dialog — not an error
          if (err instanceof DOMException && err.name === 'AbortError') {
            return;
          }
          throw err;
        } finally {
          setMergeProgress(null);
        }
        return;
      }

      const blob = await getPdf(key);
      if (!blob) {
        showToastError('PDF not found. It may have been cleared.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[StartPrintDialog] PDF download failed:', e);
      showToastError('Failed to download PDF.');
    }
  };

  const layoutInfo = useMemo(() => {
    if (!selectedTemplate || !selectedLayout) return null;
    const count = Math.max(1, Number(form.count) || 1);
    return computeLayout({
      layout: selectedLayout,
      labelCount: count,
      templateWidth: selectedTemplate.width,
      templateHeight: selectedTemplate.height,
      topOffset,
      leftOffset,
      rotation: effectivePageRotation,
    });
  }, [selectedTemplate, selectedLayout, form.count, topOffset, leftOffset, effectivePageRotation]);

  const canGeneratePreview =
    !isDraftOutputBlocked && selectedTemplate && selectedLayout && selectedPackage && Number(form.count) > 0;

  const formatPreviewContext = useMemo(() => {
    if (isDraftOutputBlocked || !selectedTemplate || !selectedPackage || selectedFormatCaps.supportsPageLayout) return null;

    const templateWithOverrides: Template = {
      ...selectedTemplate,
      pageRotation: effectivePageRotation,
      themeVariables: selectedTemplate.themeVariables?.map((variable) => ({
        ...variable,
        value: themeOverrides[variable.key] ?? variable.value,
        ...(variable.dateKind && dateStates[variable.key] ? { dateState: dateStates[variable.key] } : {}),
      })),
    };
    const { fullData, rawDataContext } = prepareRenderData({
      template: templateWithOverrides,
      coa: coaDataForPreview,
      job: form,
      product: productDataForPreview,
      common: {
        packageId: selectedPackage.id,
        packageTag: coaData.packageTag || '',
        product: resolvedProductForPreview,
        market: coaData.market || '',
        facility: coaData.facility || '',
        packagedDate: form.packagedDate || coaData.packagedDate || '',
        printedDate: form.printedDate,
        count: Number(form.count) || 1,
        jobId: form.jobId,
        jobName: form.name,
      },
    });
    const resolverContext = {
      data: fullData,
      rawDataContext,
      conditionTypeByKey: buildConditionTypeByKey(templateWithOverrides.themeVariables),
      themeVariables: templateWithOverrides.themeVariables,
      legacyImages,
      legacyFonts,
      newAssetImages,
      newAssetFonts,
      defaultFont: DEFAULT_FONT,
      isPreview: true,
      hasCoa: hasCoaForOutput,
    };
    const resolvers: FormatResolvers = {
      formatTextContent: createFormatTextContent(resolverContext),
      resolveImageUrl: createResolveImageUrl(resolverContext),
      resolveBarcodeData: createResolveBarcodeData(resolverContext),
      fetchImageBytes,
    };

    return {
      template: templateWithOverrides,
      layout: selectedLayout,
      labelData: {
        ...fullData,
        qrValue: coaData.packageTag || String(selectedPackage.id),
      },
      resolvers,
      topOffset,
      leftOffset,
    };
  }, [
    selectedTemplate,
    selectedLayout,
    selectedPackage,
    selectedFormatCaps.supportsPageLayout,
    isDraftOutputBlocked,
    effectivePageRotation,
    themeOverrides,
    dateStates,
    coaDataForPreview,
    form,
    productDataForPreview,
    coaData,
    resolvedProductForPreview,
    legacyImages,
    legacyFonts,
    newAssetImages,
    newAssetFonts,
    topOffset,
    leftOffset,
  ]);
  const FormatPreviewPanel = selectedFormatDef.PreviewPanel;

  const reelInfo = useMemo(() => {
    if (!form.splitIntoReels) return null;
    const totalCount = Number(form.count) || 1;
    const labelsPerReel = Number(form.labelsPerReel) || 1;
    const reelCount = Math.ceil(totalCount / labelsPerReel);
    return { reelCount, labelsPerReel };
  }, [form.splitIntoReels, form.count, form.labelsPerReel]);

  const saveStatusText = useMemo(() => {
    if (!isSaving) return '';

    //when using mini-reels for 1 pdf show "part" instead of "reel"
    const unit = isMiniReelMode ? 'part' : 'reel';
    const units = isMiniReelMode ? 'parts' : 'reels';

    if (reelProgress) {
      if (reelProgress.completed >= reelProgress.total) {
        return `Finalizing ${reelProgress.total} ${units}...`;
      }

      if (reelProgress.inFlight.length > 1) {
        return `Generating ${units} ${reelProgress.inFlight.join(', ')} of ${reelProgress.total} (${reelProgress.completed} done)...`;
      }

      return `Generating ${unit} ${reelProgress.inFlight[0] || reelProgress.completed + 1} of ${reelProgress.total}...`;
    }

    if (savingPhase === 'finalizing') {
      return 'Finalizing print files...';
    }

    return 'Labels are being generated. Label files will appear in this panel.';
  }, [isSaving, reelProgress, savingPhase, isMiniReelMode]);

  const saveProgressPercent = useMemo(() => {
    if (!isSaving) return null;

    if (reelProgress?.total) {
      const progress = Math.round((reelProgress.completed / reelProgress.total) * 100);
      return Math.max(6, Math.min(95, progress));
    }

    if (savingPhase === 'finalizing') {
      return 96;
    }

    return null;
  }, [isSaving, reelProgress, savingPhase]);

  const qrProgressText = useMemo(() => {
    if (!isSaving || !qrGenerationProgress?.total) return '';
    const generated = Math.min(qrGenerationProgress.total, displayedQrGenerated);
    return `${generated.toLocaleString()} / ${qrGenerationProgress.total.toLocaleString()} QRs generated`;
  }, [isSaving, qrGenerationProgress, displayedQrGenerated]);

  const reelProgressText = useMemo(() => {
    if (!isSaving || !reelProgress || !reelPageStatus) return '';

    const unit = isMiniReelMode ? 'part' : 'reel';
    const units = isMiniReelMode ? 'parts' : 'reels';

    const currentReel =
      reelProgress.inFlight[0] || Math.min(reelProgress.total, reelProgress.completed + 1);
    const reelsText = `${reelProgress.completed.toLocaleString()} / ${reelPageStatus.totalReels.toLocaleString()} ${units} complete`;
    const pagesText = `${reelPageStatus.completedPages.toLocaleString()} / ${reelPageStatus.totalPages.toLocaleString()} pages created`;

    if (reelProgress.completed < reelProgress.total) {
      return `Creating pages for ${unit} ${currentReel.toLocaleString()} of ${reelPageStatus.totalReels.toLocaleString()} · ${reelsText} · ${pagesText}`;
    }

    return `${reelsText} · ${pagesText}`;
  }, [isSaving, reelProgress, reelPageStatus, isMiniReelMode]);

  const preview = (
    <div
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {/* Preview Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div className="muted" style={{ fontSize: 12 }}>
            Print Preview
          </div>
          <div style={{ fontWeight: 700 }}>{selectedLayout?.label || 'No layout selected'}</div>
          {layoutInfo && (
            <div className="muted" style={{ fontSize: 12 }}>
              Labels/page: {layoutInfo.labelsPerPage} · Pages: {layoutInfo.pageCount}
            </div>
          )}
          {reelInfo && (
            <div className="muted" style={{ fontSize: 12 }}>
              Reels: {reelInfo.reelCount} · Labels/reel: {reelInfo.labelsPerReel}
            </div>
          )}
        </div>
        {selectedFormatCaps.supportsPageLayout && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="button secondary"
              onClick={() => handleGeneratePdf(true)}
              disabled={!canGeneratePreview || isGeneratingPdf || isLoadingAssets || isLoadingNewAssets || isSaving}
              data-testid="start-print-preview"
              style={{ whiteSpace: 'nowrap' }}
            >
              {isGeneratingPdf ? 'Generating...' : 'Generate Preview'}
            </button>
          </div>
        )}
      </div>

      {/* PDF Error */}
      {pdfError && <div className="error-text">{pdfError}</div>}

      {/* PDF Preview Container */}
      <div
        style={{
          overflow: 'auto',
          border: '1px dashed var(--border)',
          borderRadius: 12,
          padding: 12,
          background: 'var(--card-alt, #f8f9fa)',
          minHeight: 400,
        }}
      >
        {isDraftOutputBlocked ? (
          <div className="muted">Preview unavailable.</div>
        ) : !selectedFormatCaps.supportsPageLayout && formatPreviewContext ? (
          <React.Suspense fallback={<div className="muted">Loading preview...</div>}>
            <FormatPreviewPanel {...formatPreviewContext} />
          </React.Suspense>
        ) : isGeneratingPdf ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 14, marginBottom: 8 }}>
                Generating PDF preview...
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                This may take a moment for large label counts
              </div>
            </div>
          </div>
        ) : pdfPreviewUrl ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              minWidth: '100%',
            }}
          >
            <iframe
              src={`${pdfPreviewUrl}${pdfPreviewUrl.includes('#') ? '&' : '#'}zoom=300`}
              style={{
                width: '100%',
                minHeight: 600,
                height: 'auto',
                border: 'none',
                borderRadius: 8,
                background: '#fff',
                transformOrigin: 'top center',
              }}
              title="PDF Preview"
            />
          </div>
        ) : isLoadingAssets || isLoadingNewAssets ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 14, marginBottom: 8 }}>
                Loading template assets...
              </div>
            </div>
          </div>
        ) : !selectedPackage ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 14, marginBottom: 8 }}>
                {isLoadingPackages ? 'Loading packages...' : 'Select a package'}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Choose a package from the list to generate a preview
              </div>
            </div>
          </div>
        ) : !selectedTemplate ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 14, marginBottom: 8 }}>
                No template data available
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Select a template with embedded data to generate PDF preview
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 14, marginBottom: 8 }}>
                No preview available
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Click &quot;Generate Preview&quot; to create a PDF preview
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Package Info Summary (when package is selected) */}
      {selectedPackage && (
        <div
          style={{
            padding: 12,
            background: 'var(--card-alt, #f8f9fa)',
            borderRadius: 8,
            border: '1px solid var(--border)',
          }}
        >
          <div
            className="muted"
            style={{
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Selected Package
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 8,
              fontSize: 13,
            }}
          >
            <div>
              <span className="muted">Tag:</span> {selectedPackage.label}
            </div>
            <div>
              <span className="muted">Product:</span>{' '}
              <span data-testid="start-print-selected-package-product">
                {resolvedProductForPreview || '—'}
              </span>
            </div>
            <div>
              <span className="muted">Market:</span> {coaData.market || '—'}
            </div>
            <div>
              <span className="muted">Count:</span> {form.count || '—'}
            </div>
          </div>
        </div>
      )}

      {/* Theme Variables Pane */}
      {displayedThemeVariables.length > 0 && (
        <div
          data-testid="start-print-theme-variables"
          style={{
            padding: 12,
            background: 'var(--card-alt, #f8f9fa)',
            borderRadius: 8,
            border: '1px solid var(--border)',
          }}
        >
          <div
            className="muted"
            style={{
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Theme Variables
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {displayedThemeVariables.filter((variable) =>
              !variable.generated || !variable.dateKind || usedDateVariables.some((date) => date.key === variable.key)
            ).map((variable) => {
              const currentValue = themeOverrides[variable.key] ?? variable.value;
              return (
                <div
                  key={variable.id}
                  data-testid={`start-print-theme-variable-${variable.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      minWidth: 120,
                      flex: '0 0 auto',
                    }}
                  >
                    {variable.name}
                  </label>
                  <div style={{ flex: '1 1 auto', maxWidth: 200 }}>
                    <ThemeVariableInput
                      variable={variable}
                      value={variable.dateKind === 'packagedDate' ? form.packagedDate : currentValue}
                      disabled={!!initialValues?.printRunId && !isDraft}
                      testId={`start-print-theme-variable-${variable.key}-input`}
                      onChange={(newValue) => {
                        if (variable.generated && variable.dateKind) {
                          updateConfirmedDate(variable, String(newValue));
                          return;
                        }
                        setThemeOverrides((prev) => ({ ...prev, [variable.key]: newValue }));
                        if (isDraftSaved) setIsDraftSaved(false);
                      }}
                    />
                    {variable.generated && variable.dateKind &&
                      (dateStates[variable.key]?.provenance === 'manual' ||
                        dateStates[variable.key]?.provenance === 'cleared') &&
                      !(initialValues?.printRunId && !isDraft) && (
                        <button
                          type="button"
                          className="button secondary"
                          style={{ marginTop: 4, padding: '4px 8px', fontSize: 11 }}
                          onClick={() => resetConfirmedDate(variable)}
                          data-testid={`start-print-theme-variable-${variable.key}-reset`}
                        >
                          Reset to automatic
                        </button>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
          {usedDateVariables.length > 0 && (
            <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
              Each confirmed date applies to every label in this print run.
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
    <div
      className={`stack page-slide-in${isClosing ? ' page-slide-out' : ''}`}
      data-testid="start-print"
    >
      <PageHeader
        title="Start Print"
        subtitle="Label Prints"
        actions={
          <button className="button secondary" onClick={handleClose} disabled={(isSaving && !pendingPrint.waiting) || isReassignSaving} data-testid="start-print-back">
            Back
          </button>
        }
      />

      {showPrintRecovery && (
        <div className="alert" role="status" aria-live="polite" data-testid="start-print-pending">
          <p>{pendingPrint.intent?.rejection
            ? 'Your print request failed. Dismiss the failed request, then review your print settings before trying again.'
            : pendingPrint.intent?.completedPrintRunId
            ? 'Your print was saved. Open it to download the print files.'
            : 'Your print request is waiting for confirmation. You can leave this page and resume it later.'}</p>
          {(pendingPrint.intent?.rejection?.message || pendingPrint.error) && <p role="alert">{pendingPrint.intent?.rejection?.message || pendingPrint.error}</p>}
          {pendingPrint.intent?.rejection ? (
            <button className="button" onClick={() => void pendingPrint.acknowledgeRejection()} disabled={pendingPrint.resuming || isSaving} data-testid="start-print-dismiss-failed">Dismiss failed request</button>
          ) : pendingPrint.intent?.completedPrintRunId ? (
            <a className="button" href={`/prints/${pendingPrint.intent.completedPrintRunId}/edit`} data-testid="start-print-open-saved">Open saved print</a>
          ) : (
            <button className="button" onClick={() => void pendingPrint.resume()} disabled={pendingPrint.resuming || isSaving || !pendingPrint.intent} data-testid="start-print-resume">
              {pendingPrint.resuming ? 'Checking print request...' : 'Resume print request'}
            </button>
          )}
        </div>
      )}

      {selectedProductLookupFailed && (
        <div className="alert error" role="alert" aria-live="assertive" data-testid="start-print-product-lookup-error">
          <span>
            Unable to load product details for the selected package. Shelf-life dates cannot be calculated.{' '}
          </span>
          <button type="button" className="button" onClick={retrySelectedProductLookup} data-testid="start-print-product-lookup-retry">
            Retry
          </button>
        </div>
      )}

      {usesTac && isPrintContextLoaded && (
        <section aria-label="TAC print summary">
        {savedTacContextError && <div role="alert">
          {savedTacContextError} Reprinting is disabled because the saved TAC audit cannot be safely restored.
        </div>}
        {tacMarketError ? <div role="alert">
          Unable to load TAC market configuration. New TAC calculations are disabled.
          <button type="button" onClick={() => void retryTacMarkets()}>Retry TAC configuration</button>
        </div> : isLoadingTacMarkets ? <p role="status">Loading TAC market configuration...</p> : null}
        <p data-testid="tac-print-summary">TAC packages: Calculated (including edited): {tacPrintOutput.summary.calculated}; Unavailable: {tacPrintOutput.summary.unavailable}</p>
        <TacValueEditor
          key={tacContextKey}
          calculationEnabled={templateAllowsTacCalculation(selectedTemplate)}
          coa={coaData}
          adjustment={getTacAdjustment(selectedTemplate, coaData)}
          onChange={tacPackageKey(coaData) && !isSaving && !isDraftSaving && !isPrintStarted && !(initialValues?.printRunId && !isDraft)
            ? adjustment => {
              setTacEdits({ key: tacContextKey, packageKey: tacPackageKey(coaData)!, adjustment });
              setTacAcknowledgment(null);
              if (isDraftSaved) setIsDraftSaved(false);
            } : undefined}
        />
        </section>
      )}
      <FullScreenConfirmOverlay
        show={showTacGate && requiresTacAcknowledgment}
        title={`This total is ${printedTacResolution?.provenance === 'edited' ? 'edited' : 'calculated'}, not lab-reported`}
        className="tac-print-gate"
        width={680}
        testId="calculated-value-gate"
        busy={isSaving}
        confirmDisabled={tacAcknowledgment !== tacAcknowledgmentKey || pendingPrint.blocked || isDraftSaving || !isPrintContextReady || isIssuedReassignmentPending}
        cancelLabel="Back to edit"
        confirmLabel={printedTacResolution?.provenance === 'edited' ? 'Print edited value' : 'Print calculated value'}
        onCancel={() => { setShowTacGate(false); setTacAcknowledgment(null); }}
        onConfirm={() => void handleSave()}
        message={
          <div className="tac-print-gate-content">
            <p className="tac-print-gate-subtitle">
              Total Active Cannabinoids &middot; package{' '}
              <span title={selectedPackageLabel ?? undefined}>
                {selectedPackageLabel && selectedPackageLabel.length > 8 ? `...${selectedPackageLabel.slice(-6)}` : selectedPackageLabel}
              </span>
            </p>
            <div className="tac-print-gate-value">
              <div>
                <span className="tac-print-gate-caption">{tacPrintOutput.provisional ? 'May print' : 'Will print'}</span>
                <strong>{tacPrintOutput.kind === 'text' ? 'Rendered label text' : 'Total Active Cannabinoids'}</strong>
              </div>
              {tacPrintOutput.provisional && (
                <p>QR values are assigned when the run starts. These are the possible outputs;
                  the exact rendered values will be audited before files are generated.</p>
              )}
              <strong className={tacPrintOutput.kind === 'text' ? 'tac-print-gate-text' : 'tac-print-gate-percent'}>
                {tacPrintOutput.kind === 'text' ? printedTacValues.join('\n') : printedTacValues.map(value => `${value}%`).join(', ')}
              </strong>
            </div>
            <label className="tac-print-gate-acknowledgment">
              <input type="checkbox" checked={tacAcknowledgment === tacAcknowledgmentKey}
                onChange={event => setTacAcknowledgment(event.target.checked ? tacAcknowledgmentKey : null)} />
              <span>I understand this label will show a value {printedTacResolution?.provenance === 'edited' ? 'I edited' : 'Retail ID calculated'}, not the lab&apos;s.</span>
            </label>
          </div>
        }
      />

      <div className="print-files-panel" data-testid="print-files-panel">
        <div className="print-files-panel-header">Print Files</div>
        {completedPrintRun ? (
          <div className="file-download-banner">
            {form.splitIntoReels && (generatedPdfs.length > 1 || (generatedPdfs.length === 0 && Number(form.count) > 0)) ? (
              (() => {
                const slug = csvBaseName;
                const totalCount = Number(form.count) || 0;
                const perReel = Number(form.labelsPerReel) || 100;
                const reels = generatedPdfs.length > 0
                  ? generatedPdfs.map((pdf, idx) => ({ ...pdf, reelNum: idx + 1 }))
                  : Array.from({ length: Math.ceil(totalCount / perReel) }, (_, idx) => {
                      const rangeStart = idx * perReel + 1;
                      const rangeEnd = Math.min((idx + 1) * perReel, totalCount);
                      return { key: `csv-reel-${idx + 1}`, filename: '', rangeStart, rangeEnd, reelNum: idx + 1, label: `Reel ${idx + 1}` };
                    });
                return reels.map((reel) => (
                  <React.Fragment key={reel.key}>
                    {reel.filename && (
                      <div className="file-download-card" data-testid={`pdf-download-${reel.key}`}>
                        <div className="file-download-card-icon file-download-card-icon--pdf" aria-hidden="true">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                        </div>
                        <div className="file-download-card-info">
                          <span className="file-download-card-filename">{reel.filename}</span>
                          <span className="file-download-card-subtitle">Labels {reel.rangeStart} &ndash; {reel.rangeEnd}</span>
                          {mergeProgress && reel.key.endsWith('-merged') && (
                            <span className="file-download-card-subtitle" style={{ color: 'var(--accent, #0066cc)' }}>
                              {mergeProgress.phase === 'merging'
                                ? `Merging PDF... (${mergeProgress.merged}/${mergeProgress.total} parts)`
                                : mergeProgress.phase === 'serializing'
                                  ? 'Finalizing PDF...'
                                  : 'Writing to disk...'}
                            </span>
                          )}
                        </div>
                        <button
                          className="file-download-card-action"
                          onClick={() => handleDownloadPdf(reel.key, reel.filename)}
                          title={`Download ${reel.filename}`}
                          aria-label={`Download ${reel.filename}`}
                          disabled={mergeProgress !== null}
                        >
                          {mergeProgress && reel.key.endsWith('-merged') ? (
                            <span className="file-download-card-spinner" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                          )}
                        </button>
                      </div>
                    )}
                    {completedPrintRun && (
                      <div className="file-download-card" data-testid={`csv-download-reel-${reel.reelNum}`}>
                        <div className="file-download-card-icon file-download-card-icon--csv" aria-hidden="true">
                          <DownloadActionIcon />
                        </div>
                        <div className="file-download-card-info">
                          <span className="file-download-card-filename">{slug}-reel-{reel.reelNum}.csv</span>
                          <span className="file-download-card-subtitle">Labels {reel.rangeStart} &ndash; {reel.rangeEnd}</span>
                        </div>
                        <button
                          className="file-download-card-action"
                          onClick={() => handleExportReelCsv(reel.reelNum, reel.rangeStart, reel.rangeEnd)}
                          disabled={isExportingCsv}
                          title={`Download reel ${reel.reelNum} CSV`}
                          aria-label={`Download reel ${reel.reelNum} CSV`}
                          data-testid={`csv-download-reel-${reel.reelNum}-btn`}
                        >
                          {isExportingCsv ? (
                            <span className="file-download-card-spinner" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                          )}
                        </button>
                      </div>
                    )}
                  </React.Fragment>
                ));
              })()
            ) : (
              <>
                {generatedPdfs.map((pdf) => (
                  <div key={pdf.key} className="file-download-card" data-testid={`pdf-download-${pdf.key}`}>
                    <div className="file-download-card-icon file-download-card-icon--pdf" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    </div>
                    <div className="file-download-card-info">
                      <span className="file-download-card-filename">{pdf.filename}</span>
                      <span className="file-download-card-subtitle">Labels {pdf.rangeStart} &ndash; {pdf.rangeEnd}</span>
                      {mergeProgress && pdf.key.endsWith('-merged') && (
                        <span className="file-download-card-subtitle" style={{ color: 'var(--accent, #0066cc)' }}>
                          {mergeProgress.phase === 'merging'
                            ? `Merging PDF... (${mergeProgress.merged}/${mergeProgress.total} parts)`
                            : mergeProgress.phase === 'serializing'
                              ? 'Finalizing PDF...'
                              : 'Writing to disk...'}
                        </span>
                      )}
                    </div>
                    <button
                      className="file-download-card-action"
                      onClick={() => handleDownloadPdf(pdf.key, pdf.filename)}
                      title={`Download ${pdf.filename}`}
                      aria-label={`Download ${pdf.filename}`}
                      disabled={mergeProgress !== null}
                    >
                      {mergeProgress && pdf.key.endsWith('-merged') ? (
                        <span className="file-download-card-spinner" />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                      )}
                    </button>
                  </div>
                ))}
                {generatedOutput && (
                  <div className="file-download-card" data-testid="format-download-card">
                    <div
                      className={`file-download-card-icon file-download-card-icon--${selectedFormatDef.fileExtension}`}
                      aria-hidden="true"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="3" width="12" height="18" rx="1" />
                        <line x1="9" y1="7" x2="15" y2="7" />
                        <line x1="9" y1="11" x2="15" y2="11" />
                        <line x1="9" y1="15" x2="12" y2="15" />
                      </svg>
                    </div>
                    <div className="file-download-card-info">
                      <span className="file-download-card-filename">
                        {outputFilenameBase}.{selectedFormatDef.fileExtension}
                      </span>
                      <span className="file-download-card-subtitle">
                        {generatedOutput.labelCount} labels
                      </span>
                    </div>
                    <button
                      className="file-download-card-action"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = generatedOutput.url;
                        link.download = `${outputFilenameBase}.${selectedFormatDef.fileExtension}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      title={`Download ${selectedFormatDef.displayName}`}
                      aria-label={`Download ${selectedFormatDef.displayName}`}
                      data-testid="format-download-btn"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    </button>
                  </div>
                )}
                {completedPrintRun && (
                  <div className="file-download-card" data-testid="csv-download-card">
                    <div className="file-download-card-icon file-download-card-icon--csv" aria-hidden="true">
                      <DownloadActionIcon />
                    </div>
                    <div className="file-download-card-info">
                      <span className="file-download-card-filename">{csvFilename}</span>
                      <span className="file-download-card-subtitle">{Number(form.count) || 0} rows</span>
                    </div>
                    <button
                      className="file-download-card-action"
                      onClick={handleExportCsv}
                      disabled={isExportingCsv}
                      title="Download CSV"
                      aria-label="Download CSV"
                      data-testid="csv-download-btn"
                    >
                      {isExportingCsv ? (
                        <span className="file-download-card-spinner" />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="print-files-panel-empty">
            {isSaving ? (
              <div className="print-files-progress" role="status" aria-live="polite">
                <div className="print-files-progress-label">
                  <HourglassIcon style={{ marginRight: 6, flexShrink: 0 }} />
                  {saveStatusText}
                </div>
                {qrProgressText && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {qrProgressText}
                  </div>
                )}
                {reelProgressText && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {reelProgressText}
                  </div>
                )}
                <div
                  className={`print-files-progress-track${saveProgressPercent == null ? ' is-indeterminate' : ''}`}
                >
                  <div
                    className="print-files-progress-fill"
                    style={
                      saveProgressPercent == null ? undefined : { width: `${saveProgressPercent}%` }
                    }
                  />
                  <div className="print-files-progress-activity" aria-hidden="true" />
                </div>
              </div>
            ) : (
              'No files generated yet.'
            )}
          </div>
        )}
      </div>

      <div className="drawer-body">
        <fieldset className="drawer-preview start-print-input-group" disabled={arePrintInputsDisabled}>
          {preview}
        </fieldset>
        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="start-print-validation-error" />

            <fieldset
              className="form-grid start-print-input-group"
              disabled={arePrintInputsDisabled}
            >
              <div className="field full-span">
                <label className="label">Job Id </label>
                <input
                  data-testid="start-print-job-id"
                  className={`input ${errors.jobId ? 'error' : ''}`}
                  value={isDraft ? `[DRAFT] ${form.jobId || ''}` : (form.jobId || '')}
                  disabled
                  onChange={(e) => update('jobId', e.target.value)}
                  placeholder="Job ID"
                />
                {errors.jobId && <div className="error-text" data-testid="start-print-job-id-error">{errors.jobId}</div>}
              </div>

              <div className="field">
                <label className="label">
                  Job Name{' '}
                  <span
                    className="muted"
                    style={{
                      fontWeight: 400,
                      fontSize: 12,
                    }}
                  >
                    (optional)
                  </span>
                </label>
                <input
                  data-testid="start-print-job-name"
                  className="input"
                  value={form.name || ''}
                  disabled={(!!initialValues?.printRunId && !isDraft) || readOnlyJobId}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="Job Name"
                  title="PDF and CSV filenames use JobName-PackageUID, or PackageUID if the Job Name sanitizes to blank. Unsupported characters are removed and long names are shortened without splitting Unicode characters. Spaces and Package UIDs are preserved; reserved filenames get a safe underscore prefix when needed."
                />
                {errors.name && <div className="error-text" data-testid="start-print-job-name-error">{errors.name}</div>}
              </div>

              <div className="field">
                <label className="label" htmlFor={templateSelectId}>
                  Template{' '}
                  <span className="required-dot" aria-label="required">
                    *
                  </span>
                </label>
                <div ref={templateDropdownRef} style={{ position: 'relative' }}>
                  <button
                    id={templateSelectId}
                    ref={templateToggleRef}
                    type="button"
                    data-testid="template-picker-toggle"
                    className={`input ${errors.template ? 'error' : ''}`}
                    aria-haspopup="listbox"
                    aria-expanded={isTemplateDropdownOpen}
                    aria-controls={`${templateSelectId}-listbox`}
                    disabled={!!initialValues?.printRunId && !isDraft}
                    onClick={() => setIsTemplateDropdownOpen((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        setIsTemplateDropdownOpen(true);
                      }
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      cursor: (!!initialValues?.printRunId && !isDraft) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span>{form.template || 'Select template'}</span>
                    <span className="muted" aria-hidden="true">{isTemplateDropdownOpen ? '▲' : '▼'}</span>
                  </button>

                  {isTemplateDropdownOpen && (
                    <div
                      id={`${templateSelectId}-menu`}
                      aria-label="Template picker"
                      className="card"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        right: 0,
                        zIndex: 30,
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <input
                        ref={templateSearchInputRef}
                        data-testid="template-picker-search"
                        className="input search-input"
                        type="text"
                        placeholder="Search templates"
                        value={templateSearch}
                        onChange={(e) => {
                          setTemplateSearch(e.target.value);
                          setActiveTemplateIndex(0);
                        }}
                        aria-label="Search templates"
                        aria-controls={`${templateSelectId}-listbox`}
                        aria-activedescendant={
                          activeTemplateIndex >= 0
                            ? `${templateSelectId}-option-${activeTemplateIndex}`
                            : undefined
                        }
                        onKeyDown={(e) => handleSearchPickerKeyDown(e, {
                          options: filteredTemplateOptions,
                          activeIndex: activeTemplateIndex,
                          setActiveIndex: setActiveTemplateIndex,
                          selectOption: selectTemplate,
                          closePicker: closeTemplatePicker,
                        })}
                      />

                      {(isTemplateSearchPending || isSearchingTemplates) && filteredTemplateOptions.length === 0 ? (
                        <div className="muted" style={{ padding: 8, fontSize: 12 }}>
                          Searching templates...
                        </div>
                      ) : filteredTemplateOptions.length === 0 ? (
                        <div className="muted" data-testid="start-print-template-empty" style={{ padding: 8, fontSize: 12 }}>
                          No templates match your search.
                        </div>
                      ) : (
                        <div
                          id={`${templateSelectId}-listbox`}
                          data-testid="start-print-template-options" role="listbox" aria-label="Template options"
                          aria-activedescendant={
                            activeTemplateIndex >= 0
                              ? `${templateSelectId}-option-${activeTemplateIndex}`
                              : undefined
                          }
                          style={{ maxHeight: 220, overflowY: 'auto' }}
                        >
                          {filteredTemplateOptions.map((t, idx) => {
                            const isSelected = String(t.id) === String(selectedTemplateId);
                            const isActive = idx === activeTemplateIndex;
                            return (
                              <button
                                key={`${t.id}-${idx}`}
                                id={`${templateSelectId}-option-${idx}`}
                                type="button"
                                role="option"
                                tabIndex={-1}
                                aria-selected={isSelected}
                                onMouseMove={(event) => {
                                  if (latestMovingMouseEvent.current === event.nativeEvent) setActiveTemplateIndex(idx);
                                }}
                                onClick={() => selectTemplate(t)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '10px',
                                  borderRadius: 10,
                                  border: `1px solid ${isActive ? '#0b1c2a' : 'var(--border)'}`,
                                  background: isActive ? '#eef2f5' : '#fff',
                                  cursor: 'pointer',
                                  marginBottom: 6,
                                }}
                              >
                                <span>{t.name}</span>
                                {isSelected ? <span className="muted">Selected</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {templateSearchDebounced && hasMoreTemplates ? (
                        <button
                          type="button"
                          data-testid="template-picker-load-more"
                          disabled={isSearchingTemplates}
                          onClick={loadMoreTemplates}
                          style={{ width: '100%', padding: 8 }}
                        >
                          {isSearchingTemplates ? 'Loading...' : 'Load more'}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
                {errors.template && <div className="error-text" data-testid="start-print-template-error">{errors.template}</div>}
              </div>

              {/* Layout Selection */}
              <div className="field">
                <label className="label" htmlFor={layoutSelectId}>Layout</label>
                <div ref={layoutDropdownRef} style={{ position: 'relative' }}>
                  <button
                    id={layoutSelectId}
                    ref={layoutToggleRef}
                    type="button"
                    data-testid="layout-picker-toggle"
                    className="input"
                    aria-haspopup="listbox"
                    aria-expanded={isLayoutDropdownOpen && selectedFormatCaps.supportsPageLayout}
                    aria-controls={`${layoutSelectId}-listbox`}
                    disabled={!selectedFormatCaps.supportsPageLayout}
                    onClick={() => setIsLayoutDropdownOpen((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        e.preventDefault();
                        setIsLayoutDropdownOpen(true);
                      }
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      cursor: selectedFormatCaps.supportsPageLayout ? 'pointer' : 'not-allowed',
                      opacity: selectedFormatCaps.supportsPageLayout ? 1 : 0.6,
                    }}
                  >
                    <span>
                      {selectedFormatCaps.supportsPageLayout
                        ? (selectedLayout?.label || 'Select layout')
                        : 'Not available for ZPL'}
                    </span>
                    <span className="muted" aria-hidden="true">{isLayoutDropdownOpen ? '▲' : '▼'}</span>
                  </button>

                  {isLayoutDropdownOpen && selectedFormatCaps.supportsPageLayout && (
                    <div
                      id={`${layoutSelectId}-menu`}
                      aria-label="Layout picker"
                      className="card"
                      data-testid="start-print-layout-picker"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        right: 0,
                        zIndex: 30,
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <input
                        ref={layoutSearchInputRef}
                        data-testid="layout-picker-search"
                        className="input search-input"
                        type="text"
                        placeholder="Search layouts"
                        value={layoutSearch}
                        onChange={(e) => {
                          setLayoutSearch(e.target.value);
                          setActiveLayoutIndex(0);
                        }}
                        aria-label="Search layouts"
                        aria-controls={`${layoutSelectId}-listbox`}
                        aria-activedescendant={
                          activeLayoutIndex >= 0
                            ? `${layoutSelectId}-option-${activeLayoutIndex}`
                            : undefined
                        }
                        onKeyDown={(e) => handleSearchPickerKeyDown(e, {
                          options: filteredAvailableLayouts,
                          activeIndex: activeLayoutIndex,
                          setActiveIndex: setActiveLayoutIndex,
                          selectOption: (layout) => selectLayout(layout.id),
                          closePicker: closeLayoutPicker,
                        })}
                      />

                      {filteredAvailableLayouts.length === 0 ? (
                        <div className="muted" data-testid="start-print-layout-empty" style={{ padding: 8, fontSize: 12 }}>
                          No layouts match your search.
                        </div>
                      ) : (
                        <div
                          id={`${layoutSelectId}-listbox`}
                          data-testid="start-print-layout-options"
                          role="listbox"
                          aria-label="Layout options"
                          aria-activedescendant={
                            activeLayoutIndex >= 0
                              ? `${layoutSelectId}-option-${activeLayoutIndex}`
                              : undefined
                          }
                          style={{ maxHeight: 220, overflowY: 'auto' }}
                        >
                          {filteredAvailableLayouts.map((layout, idx) => {
                            const isSelected = layout.id === selectedLayoutId;
                            const isActive = idx === activeLayoutIndex;
                            return (
                              <button
                                key={layout.id}
                                id={`${layoutSelectId}-option-${idx}`}
                                type="button"
                                role="option"
                                tabIndex={-1}
                                data-testid={`start-print-layout-option-${layout.id}`}
                                aria-selected={isSelected}
                                onMouseMove={(event) => {
                                  if (latestMovingMouseEvent.current === event.nativeEvent) setActiveLayoutIndex(idx);
                                }}
                                onClick={() => selectLayout(layout.id)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '10px',
                                  borderRadius: 10,
                                  border: `1px solid ${isActive ? '#0b1c2a' : 'var(--border)'}`,
                                  background: isActive ? '#eef2f5' : '#fff',
                                  cursor: 'pointer',
                                  marginBottom: 6,
                                }}
                              >
                                <span>{layout.label || layout.id}</span>
                                {isSelected ? <span className="muted">Selected</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="field">
                <label className="label">Rotation</label>
                <select
                  data-testid="start-print-rotation"
                  className="select"
                  value={selectedFormatCaps.supportsPageLayout ? pageRotation : 'unsupported'}
                  disabled={!selectedFormatCaps.supportsPageLayout}
                  onChange={(e) => setPageRotation(Number(e.target.value) as 0 | 90 | 180 | 270)}
                >
                  {selectedFormatCaps.supportsPageLayout ? (
                    <>
                      <option value={0}>0°</option>
                      <option value={90}>90°</option>
                      <option value={180}>180°</option>
                      <option value={270}>270°</option>
                    </>
                  ) : (
                    <option value="unsupported">Not available for ZPL</option>
                  )}
                </select>
              </div>

              {/* Offsets */}
              <div className="field">
                <label className="label">Top Offset ({selectedLayout?.unit || 'in'})</label>
                <input
                  data-testid="start-print-top-offset"
                  type="number"
                  step="0.01"
                  className="input"
                  value={topOffset}
                  onChange={(e) => setTopOffset(Number(e.target.value) || 0)}
                />
              </div>

              <div className="field">
                <label className="label">Left Offset ({selectedLayout?.unit || 'in'})</label>
                <input
                  data-testid="start-print-left-offset"
                  type="number"
                  step="0.01"
                  className="input"
                  value={leftOffset}
                  onChange={(e) => setLeftOffset(Number(e.target.value) || 0)}
                />
              </div>

              <div className="field full-span">
                <label className="label">
                  Package{' '}
                  <span className="required-dot" aria-label="required">
                    *
                  </span>
                </label>
                <div>
                  <input
                    data-testid="start-print-package-search"
                    className="input search-input"
                    placeholder="Search package tag or product (min 2 chars)"
                    value={packageSearch}
                    disabled={packageMode === 'children' || isPackageSelectionLocked}
                    onChange={(e) => setSearchAndResetPage(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  {hasSavedDraftPackage && (
                    <p className="muted">This draft keeps its captured COA. To use current results or another package, delete this draft and start again.</p>
                  )}
                  <div
                    data-testid="start-print-package-results"
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: 8,
                      maxHeight: 220,
                      overflowY: 'auto',
                    }}
                  >
                    {(isLoadingPackages || isLoadingCoa) && (
                      <div className="muted" data-testid="start-print-package-loading" style={{ padding: 6 }}>
                        {isLoadingPackages ? 'Searching packages...' : 'Loading COA data...'}
                      </div>
                    )}
                    {packageSearchError && !isLoadingPackages && (
                      <div className="muted" style={{ padding: 6 }}>
                        Unable to search packages.{' '}
                        <button type="button" className="link-button" onClick={retryPackageSearch}>
                          Retry
                        </button>
                      </div>
                    )}
                    {!isLoadingPackages && !packageSearchError && pagedPackages.length === 0 && (
                      <div className="muted" data-testid="start-print-package-empty" style={{ padding: 6 }}>
                        {packageSearch.length < 2
                          ? 'Type at least 2 characters to search packages.'
                          : 'No packages match your search.'}
                      </div>
                    )}
                    {pagedPackages.map((pkg) => {
                      const isSelected =
                        (selectedPackage?.id != null && selectedPackage.id === pkg.id) ||
                        (!form.packageId && !!form.packageTag &&
                          (pkg.label === form.packageTag || pkg.coaData?.packageTag === form.packageTag));
                      const packageProductName = isSelected
                        ? (resolvedProductForPreview || pkg.coaData.productName || 'Package')
                        : (pkg.coaData.productName || 'Package');
                      const isDuplicatePackage = duplicatePackageKeys.has(pkg.id);
                      const facilityName = pkg.facilityName;
                      const facilityLicense = pkg.facilityLicense;
                      const facilityIdentifier = facilityName
                        ? `Facility: ${facilityName}`
                        : facilityLicense
                          ? `License: ${facilityLicense}`
                          : pkg.facilityId
                            ? `Facility ID: ${pkg.facilityId}`
                            : 'Facility unavailable';
                      const packageChoiceName = isDuplicatePackage
                        ? `${pkg.label} ${packageProductName} - ${facilityIdentifier}`
                        : `${pkg.label} ${packageProductName}`;
                      return (
                        <button
                          key={pkg.id}
                          type="button"
                          aria-label={isDuplicatePackage ? packageChoiceName : undefined}
                          data-testid={`start-print-package-option-${pkg.id}`}
                          data-package-label={pkg.label}
                          data-selected={isSelected}
                          onClick={() => handlePackageSelect(pkg.id)}
                          disabled={packageMode === 'children' || isPackageSelectionLocked}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px',
                            borderRadius: 10,
                            border: `1px solid ${isSelected ? '#0b1c2a' : 'var(--border)'}`,
                            background: isSelected ? '#eef2f5' : '#fff',
                            cursor: 'pointer',
                            marginBottom: 6,
                            transition:
                              'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{pkg.label}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {packageProductName}
                            {pkg.coaData.market ? ` · ${pkg.coaData.market}` : ''}
                            {!isDuplicatePackage && pkg.coaData.facility ? ` · ${pkg.coaData.facility}` : ''}
                            {isDuplicatePackage ? ` - ${facilityIdentifier}` : ''}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {!isLoadingPackages && packageCoaOptions.length > pageSize && (
                    <div
                      className="pager-mini"
                      data-testid="start-print-package-pager"
                      style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 8 }}
                    >
                      <button
                        className="pager-btn"
                        data-testid="start-print-package-prev"
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        ◀
                      </button>
                      <span className="muted" data-testid="start-print-package-page" style={{ fontSize: 12 }}>
                        Page {page} / {totalPages}
                      </span>
                      <button
                        className="pager-btn"
                        data-testid="start-print-package-next"
                        disabled={page === totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        ▶
                      </button>
                    </div>
                  )}
                </div>
                {errors.packageId && <div className="error-text" data-testid="start-print-package-error">{errors.packageId}</div>}
              </div>
            </fieldset>

             {isMultiPackageLabelGenerationEnabled() && !initialValues?.printRunId && !form.isStaged && (
              <MultiPackagePrintFeature
                source={multiPackageSource}
                onValidateSettings={validateChildSharedSettings}
                settings={selectedApiTemplate?.id && selectedTemplate ? {
                  templateId: Number(selectedApiTemplate.id),
                  jobName: form.name || null,
                  jobId: form.jobId || null,
                  packagedDate: (packagedDateEditedRef.current || usedDateVariables.some((variable) => variable.dateKind === 'packagedDate'
                    && dateStates[variable.key]?.provenance === 'manual')) && form.packagedDate
                    ? new Date(form.packagedDate).toISOString()
                    : null,
                  packagedDateExplicitlyBlank: !form.packagedDate && (packagedDateEditedRef.current
                    || usedDateVariables.some(variable => variable.dateKind === 'packagedDate' && dateStates[variable.key]?.provenance === 'cleared')),
                  labelTemplateData: JSON.stringify({
                    ...getTemplateWithOverrides(),
                    multiPackageLayoutSnapshot: selectedLayout,
                    draftPrintSettings: {
                      outputMode,
                      splitIntoReels: form.splitIntoReels === true,
                      labelsPerReel: Number(form.labelsPerReel),
                    },
                  }),
                } : null}
                products={products.filter(isActiveProduct)}
                mode={packageMode}
                onModeChange={setPackageMode}
              />
             )}

            <fieldset
              className="form-grid start-print-input-group"
              disabled={isPrintStarted || (arePrintInputsDisabled && !canReassignIssuedPrintRun)}
            >
              <div className="field full-span">
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <label className="label">Product (override)</label>
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <input
                          data-testid="start-print-product-search"
                          className="input search-input"
                          placeholder="Search products"
                          value={productSearch}
                          disabled={isReassignSaving || (!!form.printRunId && !isReassignMode)}
                          onChange={(e) => setProductSearch(e.target.value)}
                          style={{ flex: 1, marginBottom: 0 }}
                        />
                        {canReassignProduct && (
                          <button
                            type="button"
                            data-testid="start-print-reassign-toggle"
                            data-reassign-mode={isReassignMode}
                            className="button"
                            disabled={isReassignSaving || (isReassignMode && (!isProductOverridden || (!isDraft && !hasValidReassignmentProduct)))}
                            onClick={async () => {
                              if (isReassignSaving) return;
                              if (!isReassignMode) {
                                savedProductStateRef.current = {
                                  productOverrideId: form.productOverrideId,
                                  productName: form.productName,
                                  productNameCustom: form.productNameCustom,
                                  isProductOverridden,
                                  useCustomProduct,
                                };
                                setIsReassignMode(true);
                              } else {
                                setReassignConfirmPending(true);
                              }
                            }}
                            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            {isReassignSaving ? 'Saving...' : (isReassignMode ? 'Save Reassignment' : 'Reassign Labels')}
                          </button>
                        )}
                      </div>
                      <div
                        data-testid="start-print-product-results"
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 12,
                          padding: 8,
                          height: 220,
                          overflowY: 'auto',
                        }}
                      >
                        <button
                          type="button"
                          data-testid="start-print-product-option-clear"
                          data-selected={selectedProductOptionValue === ''}
                          onClick={() => handleProductOverrideSelect('')}
                          disabled={isReassignSaving || (!!form.printRunId && !isReassignMode)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px',
                            borderRadius: 10,
                            border: `1px solid ${selectedProductOptionValue === '' ? '#0b1c2a' : 'var(--border)'}`,
                            background: selectedProductOptionValue === '' ? '#eef2f5' : '#fff',
                            cursor: 'pointer',
                            marginBottom: 6,
                          }}
                        >
                          Clear
                        </button>

                        {historicalOverrideLookupFailed && (
                          <div role="alert">
                            <span>Unable to load the saved product override. </span>
                            <button type="button" className="button" onClick={retryProductCatalog}>
                              Retry saved product
                            </button>
                          </div>
                        )}

                        {historicalOverrideName && (
                          <button
                            type="button"
                            data-testid="start-print-product-option-archived"
                            disabled
                            style={{
                              ...productOptionBaseStyle,
                              border: '1px solid #0b1c2a',
                              background: '#eef2f5',
                              cursor: 'not-allowed',
                            }}
                          >
                            {historicalOverrideName} (archived)
                          </button>
                        )}

                         {filteredProductOptions.map((p) => {
                           const isSelected = selectedProductOptionValue === String(p.id);
                           return (
                             <button
                               key={p.id}
                               type="button"
                               data-testid={`start-print-product-option-${p.id}`}
                               data-selected={isSelected}
                               onClick={() => handleProductOverrideSelect(String(p.id))}
                               disabled={isReassignSaving || (!!form.printRunId && !isReassignMode)}
                               style={{
                                 ...productOptionBaseStyle,
                                 border: `1px solid ${isSelected ? '#0b1c2a' : 'var(--border)'}`,
                                 background: isSelected ? '#eef2f5' : '#fff',
                                 cursor: 'pointer',
                               }}
                             >
                               {p.displayName}
                             </button>
                           );
                         })}

                         {isLoadingProductCatalog && !filteredProductOptions.length && (
                           <div className="muted" data-testid="start-print-product-loading" style={{ padding: 6 }}>
                             Loading products...
                           </div>
                         )}

                         {!isLoadingProductCatalog && !filteredProductOptions.length && (
                           <div className="muted" data-testid="start-print-product-empty" style={{ padding: 6 }}>
                             No products match your search.
                           </div>
                         )}
                       </div>
                    </div>
                    <ProductSelectionModal
                      show={showProductOverrideModal}
                      organizationId={organizationId}
                      onCancel={() => setShowProductOverrideModal(false)}
                      onSelect={(product) => {
                        handleProductOverrideSelect(String(product.id || product.productId));
                        setShowProductOverrideModal(false);
                      }}
                    />
                  </div>
                </div>
              </div>
            </fieldset>

            <fieldset
              className="form-grid start-print-input-group"
              disabled={arePrintInputsDisabled}
            >
              <div className="field">
                <label className="label">Output</label>
                <div className="output-toggle">
                  <button
                    type="button"
                    data-testid="start-print-output-labels-csv"
                    className={`output-toggle-btn${outputMode === 'labels+csv' ? ' active' : ''}`}
                    onClick={() => updateOutputMode('labels+csv')}
                  >
                    {selectedFormatDef.displayName} + CSV
                  </button>
                  <button
                    type="button"
                    data-testid="start-print-output-csv"
                    className={`output-toggle-btn${outputMode === 'csv' ? ' active' : ''}`}
                    onClick={() => updateOutputMode('csv')}
                  >
                    CSV Only
                  </button>
                </div>
              </div>

              <div className="field">
                <label className="label">
                  Count{' '}
                  <span className="required-dot" aria-label="required">
                    *
                  </span>
                </label>
                <input
                  data-testid="start-print-count"
                  type="number"
                  min="1"
                  step="1"
                  className={`input ${errors.count ? 'error' : ''}`}
                  value={form.count}
                  disabled={!!initialValues?.printRunId && !isDraft}
                  onKeyDown={(e) => { if (e.key === '.' || e.key === 'e' || e.key === '-') e.preventDefault(); }}
                  onChange={(e) => {
                    countManuallyEditedRef.current = true;
                    update('count', e.target.value.replace(/\./g, ''));
                  }}
                />
                {errors.count && <div className="error-text" data-testid="start-print-count-error">{errors.count}</div>}
              </div>

              <div className="field">
                <label className="label">Split into reels</label>
                <select
                  data-testid="start-print-split-into-reels"
                  className="select"
                  value={form.splitIntoReels ? 'yes' : 'no'}
                  onChange={(e) => update('splitIntoReels', e.target.value === 'yes')}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {form.splitIntoReels && (
                <div className="field">
                  <label className="label">
                    Labels per reel{' '}
                    <span className="required-dot" aria-label="required">
                      *
                    </span>
                  </label>
                  <input
                    data-testid="start-print-labels-per-reel"
                    type="number"
                    min="1"
                    className={`input ${errors.labelsPerReel ? 'error' : ''}`}
                    value={form.labelsPerReel}
                    onChange={(e) => update('labelsPerReel', e.target.value)}
                  />
                  {errors.labelsPerReel && <div className="error-text" data-testid="start-print-labels-per-reel-error">{errors.labelsPerReel}</div>}
                </div>
              )}

              {(form.splitIntoReels || shouldUseMiniReels(Number(form.count) || 0)) &&
                isWorkerSupported() && (
                  <div className="field">
                    <label className="label">Parallel workers</label>
                    <input
                      data-testid="start-print-worker-count"
                      type="number"
                      min="1"
                      max={Math.min(
                        8,
                        typeof navigator !== 'undefined' && navigator.hardwareConcurrency
                          ? navigator.hardwareConcurrency
                          : 4,
                      )}
                      className="input"
                      value={workerCount}
                      onChange={(e) => {
                        const n = Math.max(
                          1,
                          Math.min(8, parseInt(e.target.value, 10) || DEFAULT_WORKER_COUNT),
                        );
                        setWorkerCount(n);
                        try {
                          localStorage.setItem('pdf-worker-count', String(n));
                        } catch {
                          /* ignore */
                        }
                      }}
                      style={{ maxWidth: 80 }}
                    />
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Threads for parallel PDF generation (1&ndash;
                      {Math.min(
                        8,
                        typeof navigator !== 'undefined' && navigator.hardwareConcurrency
                          ? navigator.hardwareConcurrency
                          : 4,
                      )}
                      )
                    </div>
                  </div>
                )}

              {!packagedDateVariable && <div className="field">
                <label className="label">Packaged Date</label>
                <input
                  data-testid="start-print-packaged-date"
                  className="input"
                  type="date"
                  value={form.packagedDate || ''}
                  disabled={!!initialValues?.printRunId && !isDraft}
                  onChange={(e) => changePackagedDate(e.target.value)}
                />
              </div>}

              <div className="field">
                <label className="label">
                  Print Date{' '}
                  <span className="required-dot" aria-label="required">
                    *
                  </span>
                </label>
                <input
                  data-testid="start-print-print-date"
                  className={`input ${errors.printedDate ? 'error' : ''}`}
                  value={form.printedDate || ''}
                  disabled
                  onChange={(e) => update('printedDate', e.target.value)}
                />
                {errors.printedDate && <div className="error-text" data-testid="start-print-print-date-error">{errors.printedDate}</div>}
              </div>
            </fieldset>

            {isPrintStarted && (
              <p className="print-files-panel-empty" style={{ marginTop: 8 }}>
                Files will be available for download in the Print Files panel at the top of this
                page
              </p>
            )}

            {hasUnresolvedDraftSave && (
              <p className="error-text" role="alert" data-testid="start-print-draft-save-recovery">
                {DRAFT_SAVE_IDENTITY_ERROR}
              </p>
            )}
            {draftCoaUnavailable && (
              <p className="error-text" role="alert" data-testid="start-print-draft-coa-error">
                This draft has no readable captured COA. Delete the draft and start again.
              </p>
            )}
            {reuseIssuedCoa && !issuedPrintCoa && (
              <p className="error-text" role="alert" data-testid="start-print-saved-coa-error">
                This print&apos;s saved lab information could not be loaded. Reopen the print to try again.
              </p>
            )}
            {reuseIssuedStagedCoa && !issuedStagedPrintCoa && (
              <p className="error-text" role="alert" data-testid="start-print-saved-staged-coa-error">
                This print&apos;s saved staged lab information could not be loaded. Reopen the print to try again.
              </p>
            )}
            {form.isStaged && isStagedCoaDataInvalid && (
              <p className="error-text" role="alert">
                Staged COA data could not be read. Refresh the page before saving.
              </p>
            )}
            <DialogFooter>
              {isPrintStarted ? (
                <button className="button" data-testid="start-print-close" onClick={handleClose} disabled={isReassignSaving}>
                  Close
                </button>
              ) : (
                <>
                  <button
                    className="button secondary"
                    data-testid="start-print-cancel"
                    onClick={handleClose}
                    disabled={(isSaving && !pendingPrint.waiting) || isDraftSaving || isReassignSaving}
                  >
                    Cancel
                  </button>
                  {packageMode === 'source' && !(initialValues?.printRunId && !isDraft) && (
                    <button
                      className="button secondary"
                      data-testid="start-print-save-draft"
                      onClick={handleSaveDraft}
                      disabled={pendingPrint.blocked || hasUnresolvedDraftSave || isSaving || isDraftSaving || isDraftSaved || !isDraftTemplateReady || isLoadingInitialPrintContext || (hasSavedDraftPackage && !selectedPackage) || draftCoaUnavailable || isLoadingCoa || !isStagedContextReady}
                    >
                      {isDraftSaving ? 'Saving...' : (isDraftSaved ? 'Draft Saved' : 'Save Draft')}
                    </button>
                  )}
                  {packageMode === 'source' && <button
                    className="button"
                    data-testid="start-print-submit"
                    onClick={handleSave}
                    disabled={pendingPrint.blocked || isSaving || isDraftSaving || !isPrintContextReady || isIssuedReassignmentPending}
                  >
                    Start Print
                  </button>}
                </>
              )}
            </DialogFooter>
            {isDraft && form.printRunId && !isPrintStarted && (
              <div className="draft-delete-row">
                <button
                  className="button danger"
                  data-testid="start-print-delete-draft"
                  onClick={() => setShowDeleteDraftConfirm(true)}
                  disabled={isSaving || isDraftSaving}
                >
                  Delete Draft
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {reassignConfirmPending && (() => {
      const isArchivedOverride = Boolean(resolvedOverrideProduct && !selectedListOverrideProduct);
      const overrideName = resolvedOverrideProduct?.name
        || productOptions.find((p) => p.id === form.productOverrideId)?.name;
      const productName = form.productOverrideId
        ? (overrideName
          ? (isArchivedOverride ? `${overrideName} (an archived product)` : overrideName)
          : 'the selected product')
        : (form.productName || 'the selected product');
      return (
        <FullScreenConfirmOverlay
          show={true}
          title="Reassign Labels"
          message={`Any product that previously was labeled with this print run will now show ${productName} on the landing page. Is this what you want?`}
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          testId="reassign-confirm"
          onConfirm={async () => {
            if (isReassignSaving) return;
            setReassignConfirmPending(false);
            await executeReassignment();
          }}
          onCancel={() => {
            setReassignConfirmPending(false);
            // RID-2327: cancelling confirmation returns to the pending selection.
            if (!isDraft) return;
            setIsReassignMode(false);
            if (savedProductStateRef.current) {
              const s = savedProductStateRef.current;
              setForm((prev) => ({
                ...prev,
                productOverrideId: s.productOverrideId,
                productName: s.productName,
                productNameCustom: s.productNameCustom,
              }));
              setIsProductOverridden(s.isProductOverridden);
              setUseCustomProduct(s.useCustomProduct);
              savedProductStateRef.current = null;
            }
          }}
        />
      );
    })()}
    {showDeleteDraftConfirm && (
      <FullScreenConfirmOverlay
        show={true}
        title="Delete Draft"
        message="Are you sure you would like to delete this draft?"
        confirmLabel="Yes"
        cancelLabel="No"
        busy={isDeletingDraft}
        testId="delete-draft-confirm"
        onConfirm={handleDeleteDraft}
        onCancel={() => setShowDeleteDraftConfirm(false)}
      />
    )}
    </>
  );
}
